/**
 * The app — one full-screen month view built around the payday cycle:
 *   · Payday hand-off banner (confirm carryover when salary lands)
 *   · Leftover hero figure (actual, per-bank truth)
 *   · A card per bank: actual now, still to pay, leftover, Import statement
 *   · Monthly Check — approvable suggestions (transfers, trims, warnings)
 *   · Items grouped income → expenses, tick to pay/receive, export CSV
 * Everything from the private instance, made smoother: fewer screens, every
 * suggestion is one tap, and imports live on the bank card where they belong.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import MonthPlan from '../components/MonthPlan.jsx';
import DashboardSkeleton from '../components/DashboardSkeleton.jsx';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { fmtR, unitRate, spentOf, spendBreakdown, moneySymbol } from '../money.js';
import { ask } from '../components/Ask.jsx';
import CategoryPicker from '../components/CategoryPicker.jsx';
import AIAction from '../components/AIAction.jsx';
import FormulaOptions from '../components/FormulaOptions.jsx';
// (useState is also used by ItemList's settle-choice dialog below)
import { api, download } from '../api.js';
import { useAuth } from '../App.jsx';
import AddExpense from '../components/AddExpense.jsx';
import { useAccordion } from '../components/AccordionRegister.jsx';
import VoiceButton from '../components/VoiceButton.jsx';
import SearchField from '../components/SearchField.jsx';
import DetailCard from '../components/DetailCard.jsx';
import ReceiptOverlay from '../components/ReceiptOverlay.jsx';
import CashSpend from '../components/CashSpend.jsx';
import SimpleHome from '../components/simple/SimpleHome.jsx';
import { BudgetViewSwitch, TopViewSwitch } from '../components/simple/viewMode.jsx';

// Ordinal suffix for a day-of-month ("25" -> "th"), for transfer move lines.
const ordSuffix = (n) => { const t = n % 100; if (t >= 11 && t <= 13) return 'th'; const u = n % 10; return u === 1 ? 'st' : u === 2 ? 'nd' : u === 3 ? 'rd' : 'th'; };
/** Two-letter bank monogram (TymeBank→TB, Discovery Bank→DB, Capitec→C):
 *  a branded stand-in for a logo so banks read apart at a glance. */
const bankMonogram = (name = '') =>
  (name.replace(/([a-z])([A-Z])/g, '$1 $2').split(/\s+/).filter(Boolean)
       .map((w) => w[0]).join('').slice(0, 2) || '?').toUpperCase();

// Avatar initials: name first, email only as a fallback (matches AppNav).
const avatarInitials = (me) => {
  const name = ((me?.first_name || '') + ' ' + (me?.last_name || '')).trim();
  if (name) return name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (me?.email || 'U').slice(0, 2).toUpperCase();
};
import ImportModal from '../components/ImportModal.jsx';
import { ManageDrawer } from './Manage.jsx';
import SpendMap from '../components/SpendMap.jsx';
import PinPreview from '../components/PinPreview.jsx';

const thisMonth = () => new Date().toISOString().slice(0, 7);

// Shift a "YYYY-MM" string by n calendar months (wraps the year). Powers the
// header's arrows so paging between months is a single tap, not a date-picker
// fiddle — asked for directly in review.
const shiftMonth = (m, n) => {
  const mm = /^(\d{4})-(\d{2})$/.exec(m || '');
  if (!mm) return m;
  const d = new Date(Number(mm[1]), Number(mm[2]) - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// A calm category icon for a money row — keyword match on category/title,
// falling back to money-in / money-out. Powers the mockup's coloured lead tile.
const rowIcon = (i) => {
  const t = `${(i && i.category_name) || ''} ${(i && i.title) || ''}`.toLowerCase();
  const map = [['coffee','☕'], ['grocer','🛒'], ['food','🍔'], ['drink','🍺'], ['fuel','⛽'], ['petrol','⛽'], ['transport','🚗'], ['uber','🚗'], ['car','🚗'], ['rent','🏠'], ['home','🏠'], ['bond','🏠'], ['netflix','🎬'], ['entertain','🎬'], ['movie','🎬'], ['salon','💇'], ['hair','💇'], ['gym','💪'], ['health','💊'], ['medical','💊'], ['pharm','💊'], ['school','🎓'], ['educat','🎓'], ['tax','🧾'], ['debt','💳'], ['loan','💳'], ['saving','🐷'], ['family','👪'], ['gift','🎁'], ['phone','📱'], ['airtime','📱'], ['data','📱'], ['internet','📶'], ['wifi','📶'], ['water','💧'], ['electr','⚡'], ['insur','🛡'], ['pet','🐶'], ['cloth','👕'], ['travel','✈'], ['holiday','✈']];
  for (const [k, e] of map) if (t.includes(k)) return e;
  return i && i.kind === 'income' ? '💰' : '💸';
};

import { Editable } from '../components/SiteEdit.jsx';
import { taxPackAvailable } from '../global.js';

/*
 * PaydayHandoff - the single payday hand-off. Carries each bank's real BALANCE
 * into the new month, with per-action toggles to write off last month's unpaid
 * expenses, carry untapped income forward, and move standing shop events over.
 * One idempotent confirm; the recap shows inline.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
function PaydayHandoff({ budget, carry, onDone }) {
  // New-month payday hand-off (Design: web-newmonth-dialog). A modal: why the
  // month rolled over, the settle-last-month list, and per-bank Carry/Bank-it
  // with a live "starts month at" — one confirm runs apply_carryover.
  const mLabel = (m) => { try { return new Date(`${m}-01T00:00:00`).toLocaleDateString('en-ZA', { month: 'long' }); } catch { return m; } };
  const prevName = mLabel(carry.prev_month || budget.month);
  const nextName = mLabel(carry.next_month || budget.month);
  const pb = carry.per_bank || [];
  const [trigger, setTrigger] = useState(carry.trigger || 'salary');
  const [openings, setOpenings] = useState(() => Object.fromEntries(pb.map((b) => [b.bank, (Number(b.actual_now) || 0).toFixed(2)])));
  const [carryOn, setCarryOn] = useState(() => Object.fromEntries(pb.map((b) => [b.bank, true])));
  const [autoMode, setAutoMode] = useState(!!carry.payday_auto);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // The bank's ACTUAL balance now already holds last month's leftover, so it is
  // the opening balance as it stands. "Bank it" moves that leftover to savings.
  const banked = (b) => (carryOn[b.bank] ? 0 : Math.max(0, Number(b.leftover) || 0));
  const startsAt = (b) => (Number(openings[b.bank]) || 0) - banked(b);
  const banners = {
    salary: `Your salary landed, so ${nextName} can start. Ticking it received is what moves you over — nothing commits until you confirm.`,
    today: `Today is your set payday, so Vault offers the new month even before the deposit shows. Confirm when you’re ready — nothing commits until you do.`,
    past: `Your payday date has already passed and ${prevName} was never closed. Vault held everything and is asking now, so no spending is lost — confirm to catch up to ${nextName}.`,
  };
  const bannerLabel = {
    salary: `${carry.salary_landed_bank || 'Salary'} salary landed`,
    today: `${nextName} payday is today`,
    past: `${prevName} was never closed`,
  };
  const TRIGGERS = [['salary', 'Salary received'], ['today', 'Payday is today'], ['past', 'Payday has passed']];
  const TAGS = { unpaid: 'text-slate-300 bg-white/10', over: 'text-red-300 bg-red-500/15', done: 'text-slate-300 bg-white/10' };
  const apply = async (reviewOnly) => {
    if (reviewOnly) { setDismissed(true); return; }
    setBusy(true);
    try {
      // Each bank opens at its actual balance now; banked leftovers go to savings.
      const banks = Object.fromEntries(pb.map((b) => [b.bank, String(Number(openings[b.bank]) || 0)]));
      const bank_save = Object.fromEntries(pb.filter((b) => banked(b) > 0).map((b) => [b.bank, String(banked(b))]));
      await api(`/budget/months/${budget.id}/apply_carryover/`, { method: 'POST',
        body: { close_expenses: true, carry_income: true, rearm_rules: true, banks, bank_save, payday_auto: autoMode } });
      onDone?.();
    } catch { /* app-wide error handling */ }
    setBusy(false);
  };
  if (dismissed) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-guide="payday-handoff">
      <div className="card w-full max-w-md !p-5 my-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className="font-extrabold text-lg text-slate-100">💰 Roll over to {nextName}?</h2>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[11px] text-slate-500 text-right leading-tight">Nothing<br />commits yet</span>
            <button className="text-slate-400 hover:text-white text-lg leading-none" onClick={() => setDismissed(true)} aria-label="Close">✕</button>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-vpurple mb-1.5">What moved you here</div>
        <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
          {TRIGGERS.map(([k, l]) => (
            <button key={k} onClick={() => setTrigger(k)}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-xs transition ${trigger === k ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-400 hover:border-slate-500'}`}>{l}</button>
          ))}
        </div>
        <div className="rounded-xl border border-vpurple/40 bg-vpurple/[0.07] p-3.5 mb-4">
          <div className="text-[10px] uppercase tracking-wider text-vpurple font-semibold mb-1">{bannerLabel[trigger]}</div>
          <p className="text-sm text-slate-200 leading-relaxed">{banners[trigger]}</p>
        </div>
        <div className="flex items-baseline justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-wider text-vpurple">Settle {prevName} expenses</div>
          <div className="text-[11px] text-slate-500">{carry.settle_summary?.unpaid || 0} unpaid · {carry.settle_summary?.done || 0} done</div>
        </div>
        <div className="rounded-xl border border-edge divide-y divide-edge overflow-hidden mb-4">
          {(carry.settle_rows || []).map((r, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2.5">
              <span className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-slate-100 block truncate">{r.name}</span>
                <span className="text-[11px] text-slate-500">{r.meta}</span>
              </span>
              <span className={`text-[10px] font-semibold uppercase rounded-md px-2 py-1 shrink-0 ${TAGS[r.tag] || TAGS.unpaid}`}>{r.tag}</span>
            </div>
          ))}
          {(carry.settle_rows || []).length === 0 && <div className="px-3 py-3 text-xs text-slate-500">Nothing to settle — last month is clean.</div>}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-vpurple mb-1.5">{nextName} opening balance · per bank</div>
        <div className="grid gap-3 mb-4">
          {pb.map((b) => (
            <div key={b.bank} className="rounded-xl border border-edge p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-bold text-slate-100">{b.bank_name}</div>
                  <div className="text-[11px] text-slate-500">{prevName} leftover {fmtR(Number(b.leftover) || 0)}</div>
                </div>
                <button onClick={() => setCarryOn((c) => ({ ...c, [b.bank]: !c[b.bank] }))} data-guide="handoff-keep"
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${carryOn[b.bank] ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-400'}`}>
                  {carryOn[b.bank] ? '✓ Keep in bank' : 'Bank it → savings'}
                </button>
              </div>
              <div className="flex items-end justify-between gap-3 mt-2.5">
                <label className="block" data-guide="handoff-actual">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">Actual balance now</span>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-slate-500 text-sm">R</span>
                    <input className="bg-black/30 border border-edge rounded-lg px-2 py-1.5 text-sm w-28 text-slate-100 outline-none focus:border-vpurple tabular-nums"
                      inputMode="decimal" value={openings[b.bank]} onChange={(e) => setOpenings((o) => ({ ...o, [b.bank]: e.target.value }))} />
                  </div>
                </label>
                <span className="text-slate-500 pb-2">→</span>
                <div className="text-right" data-guide="handoff-starts">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Starts month at</div>
                  <div className="text-lg font-extrabold text-slate-100 tabular-nums mt-0.5">{fmtR(startsAt(b))}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed mb-4">
          {nextName} opens with what is really in each bank now — that balance already includes {prevName}’s leftover. Keep it in the bank, or bank the leftover into your savings pot. Recurring expenses copy in with usage reset.
        </p>
        <div className="text-[11px] text-slate-500 mb-1.5">Each month, roll over…</div>
        <div className="flex gap-2 mb-4">
          {[['ask', 'Ask me first'], ['auto', 'Do it automatically']].map(([k]) => {
            const on = (k === 'auto') === autoMode;
            return (
              <button key={k} onClick={() => setAutoMode(k === 'auto')}
                className={`flex-1 rounded-xl border py-2.5 text-sm transition ${on ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-400'}`}>
                {k === 'auto' ? 'Do it automatically' : 'Ask me first'}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <button className="flex-1 rounded-xl border border-vpurple text-vpurple font-bold py-3 hover:bg-vpurple/10 transition disabled:opacity-60"
            onClick={() => apply(false)} disabled={busy}>{busy ? 'Settling…' : `✓ Settle ${prevName} & start ${nextName}`}</button>
          <button className="rounded-xl border border-edge text-slate-300 px-4 py-3 text-sm hover:text-white whitespace-nowrap" onClick={() => apply(true)}>Review first</button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { me, logout } = useAuth();
  const [month, setMonth] = useState(null);
  const [hdrMenu, setHdrMenu] = useState(null); // 'new' | 'user' header dropdown          // resolved by /months/current/
  const seenCurrentRef = useRef(null);                // last active month the server reported
  const [cycle, setCycle] = useState(null);          // {cycle_mode, waiting_for_salary, next_month}
  const [budget, setBudget] = useState(null);
  const [summary, setSummary] = useState(null);
  const [notes, setNotes] = useState([]);
  const [carry, setCarry] = useState(null);
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [importBank, setImportBank] = useState(null);
  const [cashSpendOpen, setCashSpendOpen] = useState(false);
  const acc = useAccordion();   // shared open-row register — one open at a time, across sections
  // Banks ride the SHARED accordion register (one row open product-wide, across
  // sections) per the final design contract — opening a bank closes an open
  // expense and vice-versa. Key namespace: 'bank:<id>'.
  // Monthly Check notes start HIDDEN on every visit (owner rule): "Show them"
  // opens them for now only. Remembering the last choice left them open for
  // good once someone had clicked Show them, so it is deliberately not stored.
  const [mcOpen, setMcOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [managing, setManaging] = useState(false);
  const [manageTab, setManageTab] = useState('Banks');
  const navigate = useNavigate();
  const [moreBank, setMoreBank] = useState(null);   // bank-card overflow menu
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const o = searchParams.get('open');
    if (o === 'map') { setMapping(true); setSearchParams({}, { replace: true }); }
    else if (o === 'manage') { setManaging(true); setSearchParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [mapping, setMapping] = useState(false);
  const [mapItem, setMapItem] = useState(null);  // deep-link an expense into the map log
  // A guide starting broadcasts this — close every open overlay first so a tour
  // never spotlights the dashboard with a stale sheet/drawer on top (#guides).
  useEffect(() => {
    const close = () => { setAdding(false); setManaging(false); setMapping(false); setMapItem(null); setCashSpendOpen(false); setImportBank(null); };
    window.addEventListener('vault-close-overlays', close);
    return () => window.removeEventListener('vault-close-overlays', close);
  }, []);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      // The server decides which month we're IN (salary mode: the new month
      // only starts once its salary is ticked received).
      const cur = await api('/budget/months/current/');
      setCycle(cur);
      let m = month;
      if (!m) {
        // First load — land on whatever month the server says is active.
        m = cur.month;
      } else if (seenCurrentRef.current
                 && cur.month !== seenCurrentRef.current
                 && month === seenCurrentRef.current) {
        // The active month just advanced (e.g. salary received) and we were
        // sitting on the month that WAS current — follow it forward to the new
        // active month. Deliberate browsing of any OTHER month is left alone.
        m = cur.month;
      }
      seenCurrentRef.current = cur.month;
      if (m !== month) { setMonth(m); return; }
      const b = await api(`/budget/months/ensure/?month=${m}`);
      setBudget(b);
      const [s, r, c, bk, cat] = await Promise.all([
        api(`/budget/months/${b.id}/summary/`),
        api(`/budget/months/${b.id}/review/`),
        api(`/budget/months/${b.id}/carryover/`),
        api('/budget/banks/'),
        api('/budget/categories/'),
      ]);
      setSummary(s); setNotes(r.notes); setCarry(c); setBanks(bk); setCategories(cat);
    } catch (e) {
      setError(e.message);
    }
  }, [month]);

  useEffect(() => {
    refresh();
    // Near-real-time: re-pull every 15s and whenever the tab regains focus, so a
    // change made on mobile/watch (a unit +1, a received income) appears here
    // without a manual reload. (Websockets would make it instant — future.)
    const poll = setInterval(refresh, 8000);   // snappier cross-device sync (watch/mobile edits land within ~8s)
    const onFocus = () => refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(poll);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  const act = async (path, body) => {
    try { await api(path, { method: 'POST', body }); await refresh(); }
    catch (e) { setError(e.data?.detail || e.message); }
  };

  // Simple view (#476) always shows the active month; the full view is untouched.
  const simple = me?.view_mode === 'simple';
  useEffect(() => {
    if (simple && cycle?.month && month && month !== cycle.month) setMonth(cycle.month);
  }, [simple, cycle, month]);

  if (!budget || !summary) {
    return <DashboardSkeleton />;
  }

  // Simple view replaces the dashboard; deep links to the map / Manage still open them in full.
  if (simple) {
    return (
      <>
        <SimpleHome budget={budget} summary={summary} carry={carry} banks={banks} refresh={refresh} />
        {managing && <ManageDrawer initialTab={manageTab} onClose={() => { setManaging(false); refresh(); }} />}
        {mapping && (
          <SpendMap budget={budget} banks={banks} openItem={mapItem}
                    onClose={() => { setMapping(false); setMapItem(null); }} onChanged={refresh} />
        )}
      </>
    );
  }

  const startNext = async () => {
    try {
      const b = await api('/budget/months/start_next/', { method: 'POST' });
      setMonth(b.month);
    } catch (e) { setError(e.data?.detail || e.message); }
  };

  return (
    <div className="min-h-full w-full px-4 md:px-8 pb-28">
      <header className="flex items-center justify-between py-4 sticky top-0 bg-ink/95 backdrop-blur z-10">
        <div className="flex items-center gap-2">
          <img src="/logo.svg" alt="" className="w-8 h-8" />
          <button className="w-7 h-7 rounded-full border border-edge text-slate-400 hover:text-white hover:border-slate-500 transition grid place-items-center leading-none"
                  title="Previous month" aria-label="Previous month"
                  onClick={() => setMonth(shiftMonth(month, -1))}>‹</button>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="bg-transparent font-bold text-lg focus:outline-none w-[12.5rem]"
                 title="Jump to any month" />
          <button className="w-7 h-7 rounded-full border border-edge text-slate-400 hover:text-white hover:border-slate-500 transition grid place-items-center leading-none"
                  title="Next month" aria-label="Next month"
                  onClick={() => setMonth(shiftMonth(month, 1))}>›</button>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/app" className="text-white font-medium" title="Dashboard">
            Dashboard
          </Link>
          <button className="text-slate-400 hover:text-white" title="See where your money goes"
                  data-guide="open-map"
                  onClick={() => setMapping(true)}>
            🗺 Map
          </button>
          <Link to="/money" className="text-slate-400 hover:text-white"
                title="Money setup — every recurring definition, formula and rule in one place">
            Money
          </Link>
          <Link to="/insights" className="text-slate-400 hover:text-white"
                title="Spending graphs — by category, top expenses, by shop/place">
            Insights
          </Link>
          {/* The SARS tax pack is South African only; other countries never see the link. */}
          {taxPackAvailable() && (
            <Link to="/tax" className="text-slate-400 hover:text-white"
                  title="Tax kit — deductible expenses, logbook & the export pack">
              Tax
            </Link>
          )}
          <Link to="/manage" className="text-slate-400 hover:text-white" data-guide="open-manage">
            Manage
          </Link>
          {/* + New — one dropdown to log anything (mockup). */}
          <div className="relative">
            <button className="btn-primary !py-1.5 !px-3.5 text-sm ml-1"
                    title="Add expense, income, savings, cash or recurring"
                    onClick={() => setHdrMenu(hdrMenu === 'new' ? null : 'new')}>＋ New</button>
            {hdrMenu === 'new' && (
              <div className="absolute right-0 mt-2 w-56 bg-surface border border-edge2 rounded-xl p-1.5 shadow-2xl z-50"
                   onMouseLeave={() => setHdrMenu(null)}>
                <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
                        onClick={() => { setHdrMenu(null); setAdding(true); }}>🧾 <span>New expense</span></button>
                <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
                        onClick={() => { setHdrMenu(null); setAdding(true); }}>💰 <span>New income</span></button>
                <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
                        onClick={() => { setHdrMenu(null); setAdding(true); }}>🐷 <span>Move to savings</span></button>
                <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
                        onClick={() => { setHdrMenu(null); setCashSpendOpen(true); }}>💵 <span>Log a cash spend</span></button>
                <Link to="/money" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5"
                        onClick={() => setHdrMenu(null)}>🔁 <span>New recurring</span></Link>
              </div>
            )}
          </div>

          {/* User chip — avatar + name, dropdown holds account, exports, admin, sign out. */}
          <div className="relative">
            <button className="flex items-center gap-2 border border-edge bg-surface rounded-full pl-1 pr-2.5 py-1 hover:border-edge2 transition"
                    title="Account & options"
                    onClick={() => setHdrMenu(hdrMenu === 'user' ? null : 'user')}>
              <span className="w-7 h-7 rounded-full bg-vault-gradient grid place-items-center text-[#06121f] font-extrabold text-xs">
                {avatarInitials(me)}</span>
              <span className="text-slate-300 text-xs">{(me?.email || 'You').split('@')[0]} ▾</span>
            </button>
            {hdrMenu === 'user' && (
              <div className="absolute right-0 mt-2 w-60 bg-surface border border-edge2 rounded-xl p-1.5 shadow-2xl z-50"
                   onMouseLeave={() => setHdrMenu(null)}>
                <Link to="/account" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5"
                      onClick={() => setHdrMenu(null)}>👤 <span>Account &amp; profile</span></Link>
                {(me?.subscription_status === 'active' || me?.is_superuser) ? (
                  <>
                    <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
                            onClick={() => { setHdrMenu(null); download(`/budget/months/${budget.id}/export/`, `vault-${month}.csv`); }}>📤 <span>Export CSV</span></button>
                    <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
                            onClick={() => { setHdrMenu(null); download(`/budget/months/${budget.id}/export/xlsx/`, `vault-${month}.xlsx`); }}>📊 <span>Export Excel</span></button>
                  </>
                ) : (
                  <div className="px-3 py-2 text-[11px] text-slate-600">Exports 🔒 subscriber perk</div>
                )}
                {me?.is_superuser && (
                  <>
                    <div className="h-px bg-edge my-1 mx-1"></div>
                    <Link to="/support" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-vpurple" onClick={() => setHdrMenu(null)}>🛟 <span>Support desk</span></Link>
                    <Link to="/testing" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-vblue" onClick={() => setHdrMenu(null)}>🧪 <span>Testing</span></Link>
                    <Link to="/revenue" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-emerald-400" onClick={() => setHdrMenu(null)}>📈 <span>Revenue</span></Link>
                  </>
                )}
                <div className="h-px bg-edge my-1 mx-1"></div>
                <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-red-400 text-left"
                        onClick={() => { setHdrMenu(null); logout(); }}>↩ <span>Sign out</span></button>
              </div>
            )}
          </div>
          {/* #476 — the only new element in the full view: turn Simple view on. */}
          <TopViewSwitch />
        </div>
      </header>

      {/* New-month rule — always visible, so it's never a mystery when the
          month rolls over. Salary mode: ticking the salary starts the month. */}
      {cycle?.cycle_mode === 'salary' && cycle?.waiting_for_salary && (
        <div className="card mb-5 text-sm text-slate-300 border-vpurple/50">
          <span className="font-semibold">{cycle.next_month} is ready and waiting.</span>{' '}
          Your new month starts the moment you tick your salary as received on it —
          fresh recurring expenses are already seeded.{' '}
          <button className="text-vblue underline" onClick={() => setMonth(cycle.next_month)}>
            Open {cycle.next_month} to tick it
          </button>
        </div>
      )}
      {cycle?.cycle_mode === 'salary' && !cycle?.waiting_for_salary
        && month === cycle?.month && (
        <p className="text-xs text-slate-600 mb-4" data-guide="cycle-note">
          New months start when you tick your salary as received —{' '}
          <button className="underline" onClick={startNext}>prepare next month</button>
        </p>
      )}
      {/* Payday (fixed-day) mode: no salary tick gates the roll-over, so state the
          rule plainly and point at the settings switch (Platforms web copy). */}
      {cycle?.cycle_mode && cycle.cycle_mode !== 'salary' && (
        <p className="text-xs text-slate-600 mb-4" data-guide="cycle-note">
          Your month rolls over on your chosen payday — switch to salary-triggered months in settings if you&apos;d rather it waits for the money.
        </p>
      )}

      {/* Payday hand-off banner */}
      {carry?.pending && (
        <PaydayHandoff budget={budget} carry={carry} onDone={refresh} />
      )}
      {carry?.waiting_for_salary && (
        <div className="card mb-5 text-sm text-slate-300">
          Payday hand-off is waiting — tick your salary as received on {summary.primary_bank}
          {' '}to hand {fmtR(carry.total)} over to this month.
        </div>
      )}


      {/* Leftover hero — the CURRENT figure (balance now − still to pay): the one
          number that matters right now. Balance-now + still-to-pay give the live
          context; the month PROJECTION sits below, clearly labelled, so a
          positive "available now" and a negative outlook never read as one. */}
      {(() => {
        const pb = summary.per_bank || [];
        // FUTURE month (viewing ahead of the active cycle): no real balances yet,
        // so forecast — the PLANNED leftover once salary lands, the salary-in vs
        // planned-spend split, and which banks fall short so you can top them up.
        const isFuture = cycle && cycle.month && month > cycle.month;
        if (isFuture) {
          const mLabel = (() => { try { return new Date(`${month}-01T00:00:00`).toLocaleDateString('en-ZA', { month: 'long' }); } catch { return month; } })();
          const shortBanks = pb.filter((b) => Number(b.remaining) < 0);
          return (<>
            <section className="text-left py-6" data-guide="leftover">
              <div className="text-slate-400 text-sm">Projected leftover once {mLabel} lands</div>
              <div className="text-5xl font-extrabold mt-1 bg-vault-gradient bg-clip-text text-transparent">{fmtR(summary.leftover)}</div>
              <div className="text-xs text-slate-500 mt-1.5">
                salary in <b className="text-emerald-400">{fmtR(summary.income_coming)}</b>
                {' '}· planned spend <b className="text-red-400">{fmtR(summary.total_expense)}</b>
                {Number(summary.to_savings) > 0 && <>{' '}· to savings {fmtR(summary.to_savings)}</>}
              </div>
              {shortBanks.length > 0 && (
                <p className="text-amber-400 text-sm mt-2">
                  ⚠ {shortBanks.map((b) => `${b.bank_name} short ${fmtR(Math.abs(Number(b.remaining)))}`).join(' · ')} — top up before payday.
                </p>
              )}
              {summary.message && (
                <p className="text-slate-500 text-sm mt-2"><span className="text-slate-600">Forecast · </span>{summary.message}</p>
              )}
            </section>
            {/* #392-#394 — the month's plan per bank, with a what-if simulation. */}
            <MonthPlan budgetId={budget.id} monthLabel={mLabel} refreshKey={summary} />
          </>);
        }
        const balanceNow = pb.reduce((a, b) => a + (b.has_actual ? Number(b.actual) || 0 : 0), 0);
        // Only banks with a KNOWN balance enter the leftover (leftover_actual
        // sums actual − still-to-pay over has_actual banks). Summing still-to-pay
        // over the SAME set keeps the visible arithmetic tied: balance now −
        // still to pay = the leftover shown above it (non-negotiable #3).
        const stillToPay = pb.reduce((a, b) => a + (b.has_actual ? Number(b.left_to_pay) || 0 : 0), 0);
        return (
          <section className="text-left py-6" data-guide="leftover">
            <div className="text-slate-400 text-sm">Real leftover across your banks</div>
            <div className="text-5xl font-extrabold mt-1 bg-vault-gradient bg-clip-text text-transparent">
              {fmtR(summary.leftover_actual)}
            </div>
            <div className="text-xs text-slate-500 mt-1.5">
              balance now <b className="text-slate-300">{fmtR(balanceNow)}</b>
              {' '}· still to pay <b className="text-red-400">{fmtR(stillToPay)}</b>
            </div>
            {Number(summary.total_expense) > Number(summary.total_income) && (
              <div className="text-xs mt-1" style={{ color: '#e0a75f' }}>
                Over budget by {fmtR(Number(summary.total_expense) - Number(summary.total_income))} — income does not cover expenses
              </div>
            )}
            {Number(summary.moves_held) > 0 ? (
              <div className="text-xs text-vpurple mt-1.5">{fmtR(summary.moves_held)} of that is already promised to another of your banks this month</div>
            ) : (summary.moves && summary.moves.length > 0) ? (
              <div className="text-xs text-slate-600 mt-1.5">every scheduled move has been made — nothing is waiting to shift</div>
            ) : null}
            {summary.message && (
              <p className="text-sm text-slate-400 mt-3 pt-3 border-t border-edge/50">
                <span className="text-slate-500">Month outlook · </span>{summary.message}
              </p>
            )}
          </section>
        );
      })()}


      {/* Two-column body — banks / savings / checks on the LEFT, money in &
          out on the RIGHT, matching the approved v4 mockup. Collapses to one
          column below lg. */}
      <div className="space-y-6">
      <div className="space-y-3">
      <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mt-1">Your banks</h2>
      {/* Bank cards — stacked in the left column. */}
      <section data-guide="bank-cards" className="grid gap-3 items-start"
               style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(430px, 100%), 1fr))' }}>
        {(summary.per_bank || []).map((b) => {
          const open = acc.openKey === 'bank:' + b.bank;
          const role = b.bank_name === summary.primary_bank ? 'receiving'
                     : b.bank_name === summary.savings_bank ? 'savings' : null;
          const lv = b.profit != null ? b.profit : b.remaining;
          const short = b.has_actual && Number(lv) < 0;
          const owed = Number(b.left_to_pay) || 0;
          const funded = Number(b.allocated) || 0;
          const actualBal = Number(b.actual) || 0;
          const freePct = actualBal > 0 ? Math.round(Math.max(0, (actualBal - owed) / actualBal) * 100) : 0;
          const fundingSentence = short
            ? `Funded to ${fmtR(funded)} of ${fmtR(owed)} owed · short by ${fmtR(Math.abs(Number(lv)))}`
            : `Funded to ${fmtR(funded)} of ${fmtR(owed)} owed · ${freePct}% of this bank\u2019s money is still free`;
          const surplusBank = short ? (summary.per_bank || [])
            .filter((x) => x.bank !== b.bank && x.has_actual)
            .map((x) => ({ name: x.bank_name, spare: Number(x.profit != null ? x.profit : x.remaining) }))
            .filter((x) => x.spare > 0)
            .sort((a, c) => c.spare - a.spare)[0] : null;
          return (
          <div key={b.bank} className={`card ${open ? 'col-span-full' : ''} ${short ? 'border border-amber-400/50' : ''}`}>
            {/* CONTRACTED by default — one tap expands. */}
            <button className="w-full flex items-center gap-3 text-left"
                    onClick={() => acc.toggle('bank:' + b.bank)}>
              <span className="w-9 h-9 shrink-0 rounded-xl bg-surface2 border border-edge grid place-items-center text-xs font-extrabold tracking-wide text-slate-300 uppercase">
                {bankMonogram(b.bank_name)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold truncate">{b.bank_name}</h3>
                  {role && <span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0 border ${role === 'receiving' ? 'text-vblue border-vblue/40' : 'text-vpurple border-vpurple/40'}`}>{role}</span>}
                  {short && <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0 border text-amber-400 border-amber-400/50">SHORT</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">still to pay {fmtR(b.left_to_pay)}</div>
                {Number(b.pending_out) > 0 && <div className="text-[11px] text-vpurple mt-0.5 truncate">⇄ {fmtR(b.pending_out)} leaves{b.move_day ? ` on the ${b.move_day}${ordSuffix(b.move_day)}` : ' this month'}</div>}
                {Number(b.pending_in) > 0 && <div className="text-[11px] text-vpurple mt-0.5 truncate">⇄ {fmtR(b.pending_in)} arrives{b.move_day ? ` on the ${b.move_day}${ordSuffix(b.move_day)}` : ' this month'}</div>}
              </div>
              <div className="text-right shrink-0">
                {b.has_actual
                  ? <div className={`font-bold ${Number(lv) < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{fmtR(lv)}</div>
                  : <div className="font-bold text-amber-400">Set balance</div>}
                <div className="text-[10px] text-slate-500">{b.has_actual ? 'leftover now' : 'not linked'}</div>
              </div>
              <span className="text-slate-500 shrink-0 text-sm">{open ? '▾' : '▸'}</span>
            </button>
            {open && (<>
            {/* Mockup stat grid: Actual/Still/Leftover, Income/Funded/Role. */}
            {/* Metrics reflow with the card width (design metricGrid:
                auto-fit minmax 170px) — no media query, correct at 380px. */}
            <div className="mt-3" style={{ display: 'grid', gap: '8px', gridTemplateColumns: 'repeat(auto-fit,minmax(min(170px,100%),1fr))' }}>
              <div className="rounded-xl border border-edge bg-surface2 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Actual now</div>
                <BankActual budget={budget} bank={b} onChange={refresh} />
              </div>
              <div className="rounded-xl border border-edge bg-surface2 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Still to pay</div>
                <div className="text-base font-bold text-red-400 mt-0.5">{fmtR(b.left_to_pay)}</div>
              </div>
              <div className="rounded-xl border border-edge bg-surface2 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Leftover</div>
                <div className={`text-base font-bold mt-0.5 ${Number(lv) < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{fmtR(lv)}</div>
              </div>
              <div className="rounded-xl border border-edge bg-surface2 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Income in</div>
                <div className="text-base font-bold mt-0.5">{fmtR(b.income)}</div>
              </div>
              <div className="rounded-xl border border-edge bg-surface2 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Funded to</div>
                <div className="text-base font-bold mt-0.5">{fmtR(b.allocated || 0)}</div>
              </div>
              <div className="rounded-xl border border-edge bg-surface2 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Role</div>
                <div className="text-sm font-semibold mt-0.5">{role === 'receiving' ? 'Salary bank' : role === 'savings' ? 'Savings' : 'Everyday'}</div>
              </div>
            </div>
            {(() => { const tot = Number(b.expense) || Number(b.income) || 0; const paid = Math.max(0, tot - Number(b.left_to_pay)); const pct = tot > 0 ? Math.min(100, Math.max(0, (paid / tot) * 100)) : 0; return (
              <div className="h-1.5 rounded-full bg-black/40 mt-3 overflow-hidden"><div className="h-full bg-gradient-to-r from-vblue to-cyan-300" style={{ width: `${pct}%` }} /></div>
            ); })()}
            {b.bank_name === summary.primary_bank && Number(summary.funds_out_total) > 0 && (
              <div className="text-xs text-vblue mt-2">Funds the other banks: {fmtR(summary.funds_out_total)} <span className="text-emerald-400">· paid in {fmtR(summary.funds_out_paid)}</span></div>
            )}
            <div className="text-[11px] text-slate-500 mt-2">{fundingSentence}</div>
            {short && surplusBank && (
              <div className="text-[11px] text-vpurple mt-1.5 leading-relaxed">
                {b.bank_name} is short {fmtR(Math.abs(Number(lv)))}. {surplusBank.name} has {fmtR(surplusBank.spare)} spare — Monthly Check offers that transfer in one tap.
              </div>
            )}
            {/* All eight bank actions, ranked (Pass 5). Import + Move money accent; Move to savings (savings only), Set balance, Set role, Rename, View statements; Archive red. */}
            <div className="flex gap-2 mt-3 flex-wrap">
              <button data-guide="import-statement" className="min-h-[42px] px-3 rounded-xl border border-vblue text-vblue text-sm hover:bg-vblue/10 transition" onClick={() => setImportBank(b)}>📥 Import statement</button>
              <button className="min-h-[42px] px-3 rounded-xl border border-vblue text-vblue text-sm hover:bg-vblue/10 transition" onClick={() => navigate('/manage?tab=Banks')}>⇄ Move money</button>
              {role === 'savings' && (
                <button className="min-h-[42px] px-3 rounded-xl border border-vpurple/60 text-vpurple text-sm hover:bg-vpurple/10 transition"
                        onClick={async () => {
                          const v = await ask({ title: 'Move to savings', message: 'It leaves your bank balances (highest first) and lands in the 🐷 pot. Available NOW: ' + fmtR(summary.leftover_actual) + '.', input: true, value: Number(summary.leftover_actual) > 0 ? summary.leftover_actual : '', ok: 'Move it' });
                          if (v && Number(v) > 0) { await api(`/budget/months/${budget.id}/save_to_savings/`, { method: 'POST', body: { amount: v } }); refresh(); }
                        }}>🐷 Move to savings…</button>
              )}
              <button className="min-h-[42px] px-3 rounded-xl border border-edge text-sm hover:border-vblue transition"
                      onClick={async () => {
                        const v = await ask({ title: `Set ${b.bank_name} balance`, message: 'What is actually in this bank right now?', input: true, value: b.has_actual ? b.actual : '', ok: 'Set balance' });
                        if (v !== null && v !== undefined && String(v) !== '') { await api(`/budget/months/${budget.id}/set_bank/`, { method: 'POST', body: { bank: b.bank, remaining: v } }); refresh(); }
                      }}>Set balance</button>
              <button className="min-h-[42px] px-3 rounded-xl border border-edge text-sm hover:border-vblue transition" onClick={() => navigate('/manage?tab=Banks')}>Set role</button>
              <button className="min-h-[42px] px-3 rounded-xl border border-edge text-sm hover:border-vblue transition" onClick={() => navigate('/manage?tab=Banks')}>Rename</button>
              <button className="min-h-[42px] px-3 rounded-xl border border-edge text-sm hover:border-vblue transition" onClick={() => navigate('/manage?tab=Banks')}>View statements</button>
              <button className="min-h-[42px] px-3 rounded-xl border border-red-400/50 text-red-400 text-sm hover:bg-red-400/10 transition" onClick={() => navigate('/manage?tab=Banks')}>Archive</button>
            </div>
            </>)}
          </div>
          );
        })}
      </section>

      {summary.moves && summary.moves.length > 0 && (
        <div>
          <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-vpurple mb-2 flex items-center justify-between">
            <span>⇄ Moves this month</span>
            <span className="normal-case tracking-normal text-[10px] text-vpurple/80">{summary.moves.filter((m) => m.moved).length} of {summary.moves.length} made</span>
          </h2>
          <div className="card border border-vpurple/30 bg-vpurple/5">
            <div className="flex flex-col gap-2">
              {summary.moves.map((m) => (
                <div key={m.id} className="flex items-center gap-3">
                  <button className={`w-6 h-6 shrink-0 rounded-full border grid place-items-center text-[11px] transition ${m.moved ? 'bg-vpurple border-vpurple text-white' : 'border-vpurple/50 text-transparent hover:border-vpurple'}`}
                          title={m.moved ? 'Moved — tap to undo' : 'Mark this move as made'}
                          onClick={async () => { await api(`/budget/items/${m.id}/complete/`, { method: 'POST', body: { completed: !m.moved } }); refresh(); }}>✓</button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-vpurple">{m.from_name || 'a bank'} → {m.to_name || 'a bank'}</div>
                    <div className="text-[11px] text-slate-500 truncate">{m.title}{m.day ? ` · ${m.moved ? 'moved' : 'leaves'} on the ${m.day}${ordSuffix(m.day)}` : ''}</div>
                  </div>
                  <div className="text-sm font-semibold text-vpurple whitespace-nowrap">{fmtR(m.amount)}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed mt-3">Moves shift money between your banks. They never change your income, your expenses, or the number at the top.</p>
          </div>
        </div>
      )}

      {/* Savings pot · Cash · Monthly Check — three compact cards in one
          auto-fit row (mockup potGrid: repeat(auto-fit, minmax(320px,1fr))). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(320px,100%),1fr))', gap: '9px' }} className="items-start">
        {/* Savings pot */}
        <div className="card" data-guide="savings-pot-card">
          <div className="flex items-baseline gap-2">
            <span className="text-[9.5px] uppercase tracking-[0.11em] text-slate-500 mr-auto">Savings pot</span>
            <span className="text-emerald-400 text-xs border border-emerald-400/35 bg-emerald-400/10 rounded-full px-2.5 py-0.5">{fmtR(summary.savings_pot)}</span>
          </div>
          <div className="font-semibold mt-2">Set aside</div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {summary.savings_bank || 'Savings bank'}
            {Number(summary.to_savings) > 0 && ` \u00b7 +${fmtR(summary.to_savings)} this month`}
          </div>
          <div className="flex gap-2 flex-wrap mt-3">
            <button className="flex-1 min-w-[6rem] border border-emerald-400/50 text-emerald-400 rounded-xl py-2 text-sm hover:bg-emerald-400/10 transition"
                    onClick={async () => {
                      const v = await ask({ title: 'Move to savings', message: 'Money leaves your bank balances (highest first) and lands in the \ud83d\udc37 pot. Available NOW: ' + fmtR(summary.leftover_actual) + '.', input: true, value: Number(summary.leftover_actual) > 0 ? summary.leftover_actual : '', ok: 'Move it' });
                      if (v && Number(v) > 0) { await api(`/budget/months/${budget.id}/save_to_savings/`, { method: 'POST', body: { amount: v } }); refresh(); }
                    }}>Move in</button>
            <button className="flex-1 min-w-[6rem] border border-emerald-400/50 text-emerald-400 rounded-xl py-2 text-sm hover:bg-emerald-400/10 transition"
                    onClick={async () => {
                      const v = await ask({ title: 'Withdraw from savings', message: 'Take money out of the \ud83d\udc37 pot back to a bank.', input: true, ok: 'Withdraw' });
                      if (!(v && Number(v) > 0)) return;
                      try {
                        await api('/budget/savings-transactions/', { method: 'POST', body: { kind: 'withdrawal', amount: v } });
                        refresh();
                      } catch (e) { setError(e.data?.detail || e.message || 'Could not withdraw from savings.'); }
                    }}>Withdraw</button>
            <button className="flex-1 min-w-[6rem] border border-edge rounded-xl py-2 text-sm hover:border-vpurple transition"
                    onClick={() => navigate('/manage?tab=Savings')}>Ledger & target</button>
          </div>
        </div>
        {/* Cash (money spots) */}
        <div className="card">
          <div className="flex items-baseline gap-2">
            <span className="text-[9.5px] uppercase tracking-[0.11em] text-slate-500 mr-auto">Cash (money spots)</span>
            <span className="text-slate-300 text-xs border border-edge rounded-full px-2.5 py-0.5">{fmtR(summary.cash_total || 0)}</span>
          </div>
          <div className="font-semibold mt-2">Wallets &amp; spots</div>
          <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">Receive, spend and log the cash you hold on hand.{Number(summary.cash_spots) ? ` ${summary.cash_spots} spot${Number(summary.cash_spots) === 1 ? '' : 's'}.` : ' 0 spots.'}</div>
          <div className="mt-3 flex gap-2">
            {Number(summary.cash_spots) > 0 && (
              <button data-guide="cash-spend-top" className="flex-1 border border-vblue text-vblue rounded-xl py-2 text-sm hover:bg-vblue/10 transition"
                      onClick={() => setCashSpendOpen(true)}>💵 Spend cash</button>
            )}
            <button className={`${Number(summary.cash_spots) > 0 ? 'flex-1' : 'w-full'} border border-edge text-slate-300 rounded-xl py-2 text-sm hover:border-vblue hover:text-vblue transition`}
                    onClick={() => navigate('/manage?tab=Cash')}>
              {Number(summary.cash_spots) > 0 ? 'Manage money spots' : 'Add a money spot'}
            </button>
          </div>
        </div>
        {/* Monthly Check (compact) */}
        <div className="card">
          <div className="flex items-baseline gap-2">
            <span className="text-[9.5px] uppercase tracking-[0.11em] text-slate-500 mr-auto">Monthly check</span>
            <span className="text-slate-300 text-xs border border-edge rounded-full px-2.5 py-0.5">{notes.length}</span>
          </div>
          {(() => {
            const acts = notes.filter((n) => n.suggest_transfer || n.trim || n.raise || n.save || n.carryover).length;
            return (
              <>
                <div className="font-semibold mt-2">{notes.length} note{notes.length === 1 ? '' : 's'}{notes.length ? ` \u00b7 ${acts} one-tap \u00b7 ${notes.length - acts} need a look` : ''}</div>
                <div className="text-xs text-slate-500 mt-0.5">{notes.length ? 'Some clear in a tap, some want a decision.' : 'Nothing to check — all good.'}</div>
              </>
            );
          })()}
          <div className="mt-3">
            <button className="w-full border border-vpurple/60 text-vpurple rounded-xl py-2 text-sm hover:bg-vpurple/10 transition"
                    onClick={() => setMcOpen((v) => !v)}>
              {mcOpen ? 'Hide' : 'Show them'}
            </button>
          </div>
        </div>
      </div>
      {/* Monthly Check expanded — full width below the row (a choice, not default). */}
      {mcOpen && notes.length > 0 && (() => {
        const renderNote = (n) => (
          <div key={n.key} className="card !p-4 flex items-start gap-3">
            <p className="flex-1 text-sm text-slate-300">{n.text}</p>
            {n.suggest_transfer && (
              <button className="btn-primary !py-1.5 !px-3 text-sm shrink-0"
                      onClick={() => act(`/budget/months/${budget.id}/move_funds/`, n.suggest_transfer)}>Approve</button>
            )}
            {n.trim && (
              <button className="btn-primary !py-1.5 !px-3 text-sm shrink-0"
                      onClick={() => act(`/budget/items/${n.trim.item_id}/set_amount/`, { amount: n.trim.suggested, scope: 'global' })}>Apply trim</button>
            )}
            {n.raise && (
              <button className="btn-primary !py-1.5 !px-3 text-sm shrink-0"
                      onClick={() => (n.raise.calculation
                        ? act(`/budget/items/${n.raise.item_id}/set_calc/`, { calculation: n.raise.calculation, scope: 'global' })
                        : act(`/budget/items/${n.raise.item_id}/set_amount/`, { amount: n.raise.suggested, scope: 'global' }))}>Raise budget</button>
            )}
            {n.save && (
              <button className="btn-primary !py-1.5 !px-3 text-sm shrink-0"
                      onClick={() => act(`/budget/months/${budget.id}/save_to_savings/`, { amount: n.save.amount })}>Save it</button>
            )}
            <button className="text-slate-600 hover:text-slate-300 shrink-0" title="Dismiss for good"
                    onClick={() => act(`/budget/months/${budget.id}/dismiss_note/`, { key: n.key })}>✕</button>
          </div>
        );
        return <div className="grid gap-2 mt-3" data-guide="monthly-check">{notes.map(renderNote)}</div>;
      })()}
      </div>

            {/* Right column — money in (top) then money out. */}
      <div>
      <ItemList budget={budget} banks={banks} summary={summary}
                categories={categories} onChange={refresh}
                onCashSpend={() => setCashSpendOpen(true)}
                onOpenMap={(it) => { setMapItem(it); setMapping(true); }} />
      </div>
      </div>

      <GetApps />

      {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

      {/* Floating add button — thumb-reach on mobile */}
      <button data-guide="add-expense"
              className="fixed bottom-6 right-6 btn-primary !rounded-full w-14 h-14 text-2xl shadow-lg"
              onClick={() => setAdding(true)} aria-label="Log an expense">
        +
      </button>

      {importBank && (
        <ImportModal budget={budget} bank={importBank} banks={banks} categories={categories}
                     onClose={() => setImportBank(null)}
                     onApplied={() => { setImportBank(null); refresh(); }} />
      )}
      {cashSpendOpen && (
        <CashSpend items={budget?.items || []} onClose={() => setCashSpendOpen(false)} onDone={refresh} />
      )}
      {adding && (
        <AddExpense budget={budget} banks={banks} categories={categories}
                    onClose={() => setAdding(false)}
                    onSaved={() => { setAdding(false); refresh(); }} />
      )}
      {managing && (
        <ManageDrawer initialTab={manageTab} onClose={() => { setManaging(false); refresh(); }} />
      )}
      {mapping && (
        <SpendMap budget={budget} banks={banks} openItem={mapItem}
                  onClose={() => { setMapping(false); setMapItem(null); }} onChanged={refresh} />
      )}

      {/* App footer: just the maker's mark — the full legal footer lives on
          the home page, not inside the budget. */}
      <footer className="mt-10 pb-4 text-center text-xs text-slate-600">
        <a href="https://mare-greyling.com" target="_blank" rel="noreferrer"
           className="hover:text-slate-400 underline">Thevertex</a>
      </footer>
    </div>
  );
}

/**
 * Click-to-edit "actual now" on a bank card: what's REALLY in the account
 * right this minute. Type it (off your banking app's balance) or let a
 * statement import set it — both write the same per-month bank state.
 */
function BankActual({ budget, bank, onChange }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const save = async () => {
    if (val !== '') {
      await api(`/budget/months/${budget.id}/set_bank/`,
                { method: 'POST', body: { bank: bank.bank, remaining: val } });
      onChange();
    }
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="mt-3 flex items-center gap-2">
        <span className="text-2xl font-bold">R</span>
        <input autoFocus type="number" inputMode="decimal" value={val}
               onChange={(e) => setVal(e.target.value)}
               onBlur={save}
               onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
               className="input !py-1 !px-2 text-2xl font-bold w-32" placeholder={bank.actual} />
      </div>
    );
  }
  return (
    <button className="mt-3 text-2xl font-bold text-left hover:text-vblue transition"
            title="Click to set what's actually in this bank right now"
            onClick={() => { setVal(bank.has_actual ? bank.actual : ''); setEditing(true); }}>
      {bank.has_actual ? `${fmtR(bank.actual)}` : <span className="text-slate-500 text-lg">Set balance…</span>}
    </button>
  );
}

/**
 * Get-the-apps card. Store buttons activate via build-time env the day the
 * apps are published — VITE_APPSTORE_URL, VITE_PLAYSTORE_URL, and optionally
 * VITE_APK_URL for a direct Android download. Watch apps install THROUGH the
 * phone apps (Apple Watch ships inside the iOS app, Wear OS via Google Play),
 * so no separate watch download exists anywhere.
 */
function GetApps() {
  // The apps are live: fall back to the real store pages (same links as the home page).
  const ios = import.meta.env.VITE_APPSTORE_URL || 'https://apps.apple.com/za/app/id6795021332';
  const android = import.meta.env.VITE_PLAYSTORE_URL || 'https://play.google.com/store/apps/details?id=com.maregreyling.vault';
  const apk = import.meta.env.VITE_APK_URL || '';
  const Btn = ({ href, children }) => href ? (
    <a href={href} target="_blank" rel="noreferrer"
       className="border border-edge rounded-xl px-4 py-2.5 text-sm font-medium hover:border-vpurple transition">
      {children}
    </a>
  ) : (
    <span className="border border-edge rounded-xl px-4 py-2.5 text-sm text-slate-600 cursor-default"
          title="Coming soon">
      {children} · soon
    </span>
  );
  return (
    <section className="mt-10 card flex flex-wrap items-center gap-4">
      <img src="/logo.svg" alt="" className="w-10 h-10" />
      <div className="flex-1 min-w-[200px]">
        <Editable id="app.phonewatch.title" as="h2" kind="text" className="font-bold">Vault on your phone & watch</Editable>
        <p className="text-sm text-slate-400 mt-1">
          Log spends offline, scan receipts with the camera, import statements from your
          banking app's share sheet. The watch app installs with the phone app —
          quick-log and glance from your wrist.
        </p>
      </div>
      <div className="flex gap-3 flex-wrap">
        <Btn href={ios}> App Store</Btn>
        <Btn href={android}>Google Play</Btn>
        {apk && <Btn href={apk}>Android APK</Btn>}
      </div>
    </section>
  );
}

/** Receiving income is a moment: confirm how much actually landed (prefilled
 * with the budget), and when it differs, offer to update the RECURRING income
 * so future months expect the new amount. */
/** The 🐷 pot, one tap deep: two tabs (Deposit / Withdraw) that move REAL
 * money — a deposit leaves the chosen bank into the pot, a withdrawal
 * returns to it — plus this month's movements and the past-savings
 * baseline. Closes only via Close; choices, never accidents. */
function SavingsPot({ budget, banks = [], onClose, onChange }) {
  const [data, setData] = useState(null);
  const [txs, setTxs] = useState([]);
  const [tab, setTab] = useState('deposit');
  const [amt, setAmt] = useState('');
  const [note, setNote] = useState('');
  const [bank, setBank] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    const d = await api(`/budget/savings/?month=${budget.month}`);
    setData(d);
    // ALL movements (every month), newest first — so any deposit or withdrawal
    // can be deleted here, not just this month's. Delete reverses against the
    // movement's own month on the server.
    const all = await api('/budget/savings-transactions/').catch(() => []);
    setTxs(all || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const record = async () => {
    if (!amt || Number(amt) <= 0) return;
    setBusy(true); setError('');
    try {
      await api('/budget/savings-transactions/',
                { method: 'POST',
                  body: { kind: tab, amount: amt, note,
                          ...(bank ? { bank: Number(bank) } : {}) } });
      setAmt(''); setNote('');
      await load(); onChange();
    } catch (e) {
      setError(e.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };
  const setBaseline = async () => {
    const v = await ask({
      title: 'Past savings',
      message: 'Savings already banked BEFORE Vault started tracking. '
        + 'The pot = this baseline + every recorded movement.',
      input: true, value: data?.baseline ?? '', ok: 'Set baseline',
    });
    if (v !== null && v !== '') {
      await api('/budget/savings/', { method: 'PATCH',
                                      body: { opening_balance: v } });
      await load(); onChange();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center z-[70] px-6"
         data-guide-context="savings-pot">
      <div className="card max-w-md w-full max-h-[88vh] overflow-y-auto">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h3 className="font-bold">🐷 Savings pot</h3>
            <div className="text-3xl font-extrabold mt-1 bg-vault-gradient bg-clip-text text-transparent">
              {data ? fmtR(data.balance) : '…'}
            </div>
            {data && (
              <p className="text-xs text-slate-500 mt-1">
                {budget.month}: +{fmtR(data.deposits)} · −{fmtR(data.withdrawals)}
                {' · '}
                <button className="underline hover:text-slate-300" onClick={setBaseline}>
                  past savings {fmtR(data.baseline)}
                </button>
              </p>
            )}
          </div>
          <button className="w-8 h-8 rounded-full border border-edge text-slate-400
                             hover:text-white shrink-0"
                  onClick={onClose}>✕</button>
        </div>

        <div className="flex gap-1.5 mt-4 mb-2">
          {[['deposit', '💰 Deposit'], ['withdrawal', '💸 Withdraw']].map(([k, label]) => (
            <button key={k}
                    className={`flex-1 text-sm py-1.5 rounded-xl border ${
                      tab === k ? 'border-vpurple bg-vpurple/15 text-white'
                                : 'border-edge text-slate-500'}`}
                    onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </div>
        <div className="grid gap-2">
          <input className="input text-right font-medium" placeholder="Amount"
                 value={amt} onChange={(e) => setAmt(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && record()} />
          {/* The pot lives INSIDE the chosen savings bank — no bank picker:
              deposits leave it, withdrawals return to it, and what's left
              after expenses and savings is what that bank holds. */}
          <p className="text-xs text-slate-500 border border-edge rounded-xl px-3 py-2">
            {tab === 'deposit' ? 'Leaves' : 'Returns to'}{' '}
            <span className="text-slate-300">{data ? data.savings_bank : '…'}</span>
            {' '}— your savings home
          </p>
          <input className="input text-sm" placeholder="Note (optional)"
                 value={note} onChange={(e) => setNote(e.target.value)} />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button className="btn-primary !py-2" disabled={busy || !amt} onClick={record}>
            {tab === 'deposit' ? 'Deposit into the pot' : 'Withdraw from the pot'}
          </button>
          <p className="text-[11px] text-slate-600">
            {tab === 'deposit'
              ? 'The amount leaves the chosen bank and grows the pot.'
              : 'The amount leaves the pot and lands back on the chosen bank.'}
          </p>
        </div>

        <h4 className="text-xs uppercase tracking-wide text-slate-500 mt-4 mb-1">
          All movements — deposits &amp; withdrawals
        </h4>
        <div className="grid gap-1 text-sm max-h-56 overflow-y-auto">
          {txs.map((t) => (
            <div key={t.id} className="flex items-center gap-2 border border-edge
                                       rounded-lg px-3 py-1.5">
              <span className={t.kind === 'deposit' ? 'text-emerald-400' : 'text-red-400'}>
                {t.kind === 'deposit' ? '+' : '−'}{fmtR(t.amount)}
              </span>
              <span className="flex-1 text-xs text-slate-500 truncate">
                {t.note || (t.kind === 'deposit' ? 'Deposit' : 'Withdrawal')}
              </span>
              <span className="text-[10px] text-slate-600">{t.date}</span>
              <button className="text-slate-600 hover:text-red-400 text-xs shrink-0"
                      title="Delete this movement — reverses its bank move"
                      onClick={async () => {
                        if (await ask({ title: 'Delete this movement?',
                            message: 'Removes it from the pot and reverses the '
                              + 'money it moved to/from the bank.',
                            ok: 'Delete', danger: true, cancel: 'Keep it' })) {
                          await api(`/budget/savings-transactions/${t.id}/`, { method: 'DELETE' });
                          await load(); onChange();
                        }
                      }}>✕</button>
            </div>
          ))}
          {!txs.length && (
            <p className="text-slate-600 text-xs py-2">No movements yet.</p>
          )}
        </div>
        <button className="mt-4 text-sm text-slate-500 hover:text-slate-300" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

function ReceiveIncome({ item, banks = [], masterBank = null, onDone, onCancel }) {
  const [amt, setAmt] = useState(String(item.amount));
  const [bankSel, setBankSel] = useState(item.bank || '');
  // Default OFF: received income is treated as NEW money (raises the hero total)
  // — tick "Deduct from master" only when it's a portion moved from the salary
  // bank. (Was default ON, which made every off-master receive a net-zero
  // transfer and never moved the top leftover.)
  const [fromMaster, setFromMaster] = useState(false);
  const [askRecur, setAskRecur] = useState(false);
  // The deduct-from-master choice only exists when there IS a master and
  // the money is landing somewhere else.
  const offMaster = masterBank
    && Number(bankSel || item.bank || 0) !== Number(masterBank.bank || 0);
  const received = async () => {
    // Landing bank is part of RECEIVING: the salary leaves Discovery and
    // lands on TymeBank — the line must live where the money actually hit,
    // or the credit and the row end up under different bank filters.
    if (bankSel && Number(bankSel) !== Number(item.bank || 0)) {
      await api(`/budget/items/${item.id}/`,
                { method: 'PATCH', body: { bank: Number(bankSel) } });
    }
    await api(`/budget/items/${item.id}/complete/`,
              { method: 'POST',
                body: { completed: true, actual: amt,
                        from_master: offMaster ? fromMaster : false } });
    const changed = Number(amt) !== Number(item.amount);
    if (changed && item.template) setAskRecur(true);
    else onDone();
  };
  const updateRecurring = async (yes) => {
    if (yes) {
      await api(`/budget/recurring/${item.template}/`,
                { method: 'PATCH', body: { amount: amt } });
    }
    onDone();
  };
  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center z-[60] px-6"
         onClick={onCancel}>
      <div className="card max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        {!askRecur ? (
          <>
            <h3 className="font-bold">{item.title} — received 💰</h3>
            <p className="text-sm text-slate-400 mt-1">
              How much landed this month? It can differ from the budgeted {fmtR(item.amount)}.
            </p>
            <input className="input mt-3 text-right font-medium" autoFocus
                   value={amt} onChange={(e) => setAmt(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && received()} />
            {banks.length > 0 && (
              <label className="block mt-3 text-xs text-slate-500">
                Landed in
                <select className="input mt-1 text-sm" value={bankSel}
                        onChange={(e) => setBankSel(e.target.value)}>
                  <option value="">— pick the bank it landed in —</option>
                  {banks.filter((b) => !b.archived).map((b) => (
                    <option key={b.id} value={b.id}>{b.account_name ? `${b.name} \u00b7 ${b.account_name}` : b.name}</option>
                  ))}
                </select>
              </label>
            )}
            {offMaster && (
              /* A choice, never a rule: transfers from the master deduct it;
                 genuinely new money (a client payment) leaves it alone. */
              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                <input type="checkbox" checked={fromMaster}
                       onChange={() => setFromMaster((v) => !v)} />
                <span>
                  Deduct from ★ {masterBank.bank_name || 'master'}
                  <span className="block text-[11px] text-slate-500">
                    money moved from the master — untick if this is new money
                  </span>
                </span>
              </label>
            )}
            <div className="mt-3 flex gap-3">
              <button className="flex-1 btn-primary !py-2.5" onClick={received}>
                Received ✓
              </button>
              <button className="flex-1 border border-edge rounded-xl py-2.5 text-slate-400"
                      onClick={onCancel}>
                Not yet
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="font-bold">Update the recurring income?</h3>
            <p className="text-sm text-slate-400 mt-1">
              You received {fmtR(amt)} instead of {fmtR(item.amount)}. Should future
              months expect {fmtR(amt)} for “{item.title}”? This month is recorded
              either way.
            </p>
            <div className="mt-3 flex gap-3">
              <button className="flex-1 btn-primary !py-2.5" onClick={() => updateRecurring(true)}>
                Yes — from now on
              </button>
              <button className="flex-1 border border-edge rounded-xl py-2.5 text-slate-400"
                      onClick={() => updateRecurring(false)}>
                No — once-off
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Add money to an expense — EITHER or BOTH, independently: count it as
 * spend, and/or grow the formula by it (36*5 → 36*5+42). Same semantics as
 * the import review. */
function AddToExpense({ it, onChange }) {
  // Only a rate×count formula can be "grown" — for a plain expense the just
  // action is to add spend, so the grow option and its toggles never appear.
  const hasFormula = /[*]/.test(String(it.calculation || ''));
  const [amt, setAmt] = useState('');
  const [spend, setSpend] = useState(true);
  const [grow, setGrow] = useState(false);
  const [note, setNote] = useState('');
  const noteBefore = useRef('');   // pre-AI note, so AIAction's Undo restores it
  const suggestNote = async () => {
    const d = await api('/budget/places/suggest_note/', { method: 'POST',
      body: { title: it.title || '', amount: amt } });
    return d?.text || null;
  };
  const apply = async () => {
    const v = parseFloat(amt.replace(',', '.'));
    const doGrow = hasFormula && grow;
    const doSpend = !hasFormula || spend;
    if (!v || v <= 0 || (!doSpend && !doGrow)) return;
    if (doGrow) {
      const base = (it.calculation || String(it.amount)).trim();
      await api(`/budget/items/${it.id}/set_calc/`,
                { method: 'POST', body: { calculation: `${base}+${v}`, scope: 'month' } });
    }
    if (doSpend) {
      if (it.place) {
        // Placed expense: log the spend as a dated VISIT so it shows under
        // Logged and the bank deducts (server applies + reconciles). The note
        // rides along with the visit, per-spend.
        const today = new Date().toISOString().slice(0, 10);
        await api('/budget/place-visits/',
                  { method: 'POST', body: { place: it.place, item: it.id, title: it.title,
                    amount: v.toFixed(2), units: 0, on: today,
                    note: note.trim().slice(0, 200), bump: 1, confirmed: true } });
      } else {
        await api(`/budget/items/${it.id}/`,
                  { method: 'PATCH', body: { actual: (Number(it.actual || 0) + v).toFixed(2) } });
      }
    }
    if (note.trim() && !it.place) {
      await api(`/budget/items/${it.id}/`,
                { method: 'PATCH', body: { note: note.trim().slice(0, 200) } });
    }
    setAmt(''); setNote(''); onChange();
  };
  return (
    <div className="mt-2 border-t border-edge pt-2">
      <div className="flex gap-1.5 items-center flex-wrap">
        <input className="input !w-24 !py-1 text-sm text-right" placeholder="+ R…"
               value={amt} onChange={(e) => setAmt(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && apply()} />
        {hasFormula && (
          <>
            <button className={`text-xs px-2 py-1 rounded border ${
                      spend ? 'border-vblue text-vblue' : 'border-edge text-slate-500'}`}
                    onClick={() => setSpend(!spend)}>
              {spend ? '✓ ' : ''}spend
            </button>
            <button className={`text-xs px-2 py-1 rounded border ${
                      grow ? 'border-vpurple text-vpurple' : 'border-edge text-slate-500'}`}
                    onClick={() => setGrow(!grow)}>
              {grow ? '✓ ' : ''}grow formula
            </button>
          </>
        )}
        <button className="btn-primary !py-1 !px-3 text-sm"
                disabled={!amt || (hasFormula && !spend && !grow)}
                onClick={apply}>
          Add{hasFormula ? '' : ' spend'}
        </button>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <input className="input flex-1 !py-1 text-sm"
               placeholder="Note (optional) — what was it for?"
               value={note} onChange={(e) => setNote(e.target.value)} />
        <AIAction size="sm"
                  title="Suggest a description with AI"
                  run={() => { noteBefore.current = note; return suggestNote(); }}
                  onResult={setNote}
                  onUndo={() => setNote(noteBefore.current)}
                  readback="Suggested a description — edit before you add it." />
      </div>
      {hasFormula && (
        <p className="text-[10px] text-slate-600 mt-1">
          Either or both: spend uses the money; grow raises the budget's formula by it.
        </p>
      )}
    </div>
  );
}

function ItemList({ budget, banks = [], summary = null, categories = [], onChange, onOpenMap, onCashSpend }) {
  const [saveNote, setSaveNote] = useState('');   // "✓ saved…" feedback
  // A failed log / set-total explains itself in the app dialog instead of throwing.
  const setError = (message) => { if (message) ask({ title: 'Could not save', message, ok: 'OK' }); };
  const [potOpen, setPotOpen] = useState(false);  // 🐷 pot overlay
  const [q, setQ] = useState('');                 // search-as-you-type filter
  const [settling, setSettling] = useState(null);   // expense awaiting the settle choice
  const [deleting, setDeleting] = useState(null);   // item awaiting delete confirm
  const [clearing, setClearing] = useState(false);  // clear-the-month confirm
  const [receiving, setReceiving] = useState(null); // income being ticked received
  const [detail, setDetail] = useState(null);       // item detail: note + receipt
  const [liveCalc, setLiveCalc] = useState('');     // Advanced tab: live formula as you type
  const [detailVisits, setDetailVisits] = useState([]); // map visits for the open item
  const [receiptOpen, setReceiptOpen] = useState(null);  // { src, actionBase, attachedTo, applyItem? } for the shared ReceiptOverlay, or null
  const [detailRules, setDetailRules] = useState([]); // standing shop events for the open item
  const [detailNonce, setDetailNonce] = useState(0);   // bump to reload the detail's visits/rules
  const [detailPlaces, setDetailPlaces] = useState([]); // saved places, for the edit-tab location linker
  const [linkQ, setLinkQ] = useState('');
  const [mergeOpen, setMergeOpen] = useState(false); // reclassify as a spend on another expense
  useEffect(() => {
    const close = () => { setDetail(null); setPotOpen(false); setDeleting(null); setReceiving(null); setReceiptOpen(null); setMergeOpen(false); };
    window.addEventListener('vault-close-overlays', close);
    return () => window.removeEventListener('vault-close-overlays', close);
  }, []);
  const [mergeQ, setMergeQ] = useState('');
  const [receiptBusy, setReceiptBusy] = useState(false); // AI reading an attached receipt
  const [markDoneAsk, setMarkDoneAsk] = useState(null);  // after a receipt: offer to mark the expense done
  const [linkResults, setLinkResults] = useState([]);
  const [linkPreview, setLinkPreview] = useState(null); // {name,address,lat,lng,id?} to show a pin before committing
  const [editCountId, setEditCountId] = useState(null); // which item's unit count is being typed
  useEffect(() => { setLiveCalc(''); setLinkPreview(null); }, [detail?.id]);
  useEffect(() => {
    if (!detail) { setDetailVisits([]); setDetailRules([]); return; }
    api(`/budget/place-visits/?item=${detail.id}`).then((vs) =>
      setDetailVisits(Array.isArray(vs) ? vs : (vs?.results || [])))
      .catch(() => setDetailVisits([]));
    api('/budget/place-visit-rules/').then((rs) =>
      setDetailRules((rs || []).filter((r) =>
        (r.item_title || '').toLowerCase() === (detail.title || '').toLowerCase())))
      .catch(() => setDetailRules([]));
    api('/budget/places/').then((ps) =>
      setDetailPlaces(Array.isArray(ps) ? ps : (ps.results || []))).catch(() => setDetailPlaces([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id, detailNonce]);
  // Live location search in the Advanced tab: saved places filter instantly (below,
  // in the render); NEW shops/addresses resolve from the map, debounced — so typing
  // shows results without pressing Enter.
  useEffect(() => {
    const q = linkQ.trim();
    if (q.length < 2) { setLinkResults([]); return undefined; }
    const t = setTimeout(() => {
      api('/budget/places/resolve/', { method: 'POST', body: { query: q, title: detail?.title } })
        .then((r) => setLinkResults((r && r.candidates) || [])).catch(() => {});
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkQ, detail?.id]);
  const [bankFilter, setBankFilter] = useState(0);  // 0 = all banks
  const [micOn, setMicOn] = useState(false);   // mic live -> drives the toolbar's fourth AI read-back state
  const [dfilter, setDfilter] = useState('');       // Unpaid | Formula | Spend-only | Has a place (single-select)
  // Manual inter-bank transfer (records a real move_funds, not an income).
  const [xferOpen, setXferOpen] = useState(false);
  const [xferFrom, setXferFrom] = useState(0);
  const [xferTo, setXferTo] = useState(0);
  const [xferAmt, setXferAmt] = useState('');
  // EVERY active bank gets a chip — including ones with nothing in them yet,
  // so you can see at a glance that a bank is empty this month.
  const bankChips = banks.filter((b) => !b.archived).map((b) => [b.id, b.account_name ? `${b.name} \u00b7 ${b.account_name}` : b.name]);
  // Transfer NOTES (paired zero-amount rows) belong to a bank's own story:
  // they show when that bank is filtered, never in the All-banks view — the
  // money only moved, globally nothing changed.
  // The OWNER'S rule: with a ★ master crowned, only incomes on the MASTER'S
  // BANK count globally. Bank-local incomes still SHOW in the All-banks list
  // (dimmed, labelled with their bank) so they can be opened, re-paired with
  // the master, or deleted — deleting refunds the master when it was
  // deducted. They just never enter the global totals.
  const masterLine = (budget.items || []).find((i) => i.salary_primary);
  const bankLocalIncome = (i) => !!(masterLine && i.kind === 'income'
    && i.bank && i.bank !== masterLine.bank);
  // The rule, strong form: All banks shows the MASTER'S income story only.
  // Every bank-local income — received or still waiting, paired or new —
  // lives under its own bank's filter, where it's managed and ticked.
  // Future months included: an unticked August check is Capitec's business.
  // Search-as-you-type: title, category, bank and note all match.
  const ql = q.trim().toLowerCase();
  const matches = (i) => !ql
    || [i.title, i.category_name, i.bank_name, i.description]
       .some((s) => (s || '').toLowerCase().includes(ql));
  // Filter chips act on EXPENSES only — an income line always shows (greyed if
  // not received), never hidden by a chip (Pass 5 toolbar contract).
  const isFormulaItem = (i) => i.kind === 'expense' && !!(i.calculation && String(i.calculation).trim());
  const byFilter = (i) => {
    if (!dfilter || i.kind === 'income') return true;
    if (dfilter === 'Unpaid') return !i.completed;
    if (dfilter === 'Formula') return isFormulaItem(i);
    if (dfilter === 'Spend-only') return !isFormulaItem(i);
    if (dfilter === 'Has a place') return !!i.place;
    return true;
  };
  const visible = (bankFilter
    ? (budget.items || []).filter((i) => i.bank === bankFilter)
    : (budget.items || []).filter((i) => i.source !== 'transfer' && !bankLocalIncome(i)))
    .filter(matches).filter(byFilter);
  const groups = {};
  for (const i of visible) {
    // A transfer has two sides: the OUT (expense) sits in Transfers on the paying
    // bank; the IN (income) shows on the receiving bank's INCOME side as a
    // transfer note — so a move from TymeBank shows on BOTH banks' views.
    const g = i.source === 'transfer'
              ? (i.kind === 'income' ? 'Income' : 'Transfers')
            : i.kind === 'income' ? 'Income' : (i.category_name || 'Other');
    (groups[g] = groups[g] || []).push(i);
  }
  const names = ['Income',
                 ...Object.keys(groups).filter((g) => g !== 'Income' && g !== 'Transfers').sort(),
                 'Transfers'];

  // Pass 5 toolbar AI read-back — the count in every variant is the actual
  // filtered rows, so the sentence can never disagree with the list.
  const scoped = !!bankFilter;
  const scopeBankName = scoped ? (bankChips.find(([id]) => id === bankFilter)?.[1] || 'this bank') : null;
  const scopeBankPb = scoped ? (summary?.per_bank || []).find((b) => b.bank === bankFilter) : null;
  const dqTrim = q.trim();
  const expShown = visible.filter((i) => i.kind === 'expense').length;
  const incShown = visible.filter((i) => i.kind === 'income').length;
  const expTotalScope = (bankFilter
    ? (budget.items || []).filter((i) => i.bank === bankFilter && i.kind === 'expense')
    : (budget.items || []).filter((i) => i.kind === 'expense' && i.source !== 'transfer')).length;
  const scopeLeftStr = scopeBankPb ? fmtR(scopeBankPb.profit != null ? scopeBankPb.profit : scopeBankPb.remaining) : '—';
  const dAi = micOn
    ? `🎙 Heard ${dqTrim ? `“${dqTrim}”` : 'you'} — showing ${visible.length} match${visible.length === 1 ? '' : 'es'}. Say a bank, a category, a place or an amount to narrow it further.`
    : (dqTrim || dfilter)
    ? `Filtering on ${[dqTrim ? `“${dqTrim}”` : '', dfilter].filter(Boolean).join(' + ')}${scoped ? ` within ${scopeBankName}` : ''} — ${expShown} of ${expTotalScope} expenses.`
    : scoped
      ? `${scopeBankName} alone: ${expShown} expense${expShown === 1 ? '' : 's'} and ${incShown} income line${incShown === 1 ? '' : 's'}. Its own leftover is ${scopeLeftStr}.`
      : 'Say or type a shop, a category or an amount. “Groceries this week”, “anything at Gaal”, “unpaid formulas”.';

  const complete = async (item, settle) => {
    await api(`/budget/items/${item.id}/complete/`,
              { method: 'POST', body: { completed: !item.completed, settle } });
    setSettling(null);
    onChange();
  };

  // Attaching a receipt while EDITING an expense: store it, interpret it with AI
  // (the "Reading…" state proves it is working), then — if the expense is not
  // done yet — offer to mark it done right there. Interpretation is best-effort;
  // the file is already saved regardless.
  const attachAndInterpret = async (item, file) => {
    setReceiptBusy(true);
    try {
      const fd = new FormData(); fd.append('receipt', file);
      await api(`/budget/items/${item.id}/`, { method: 'PATCH', form: fd });
      try {
        const fd2 = new FormData(); fd2.append('receipt', file); fd2.append('mode', 'receipt');
        await api('/budget/items/scan_receipt/', { method: 'POST', form: fd2 });
      } catch { /* interpretation is best-effort; the receipt is already stored */ }
      onChange();
      if (!item.completed) setMarkDoneAsk(item);
    } finally { setReceiptBusy(false); }
  };

  // Formula/amount saving — mare-greyling style: anything with + - * / stores
  // as the calculation (server evaluates); plain numbers set the amount. Scope
  // is an explicit button in the overlay: this month, or everywhere.
  const saveAmount = async (item, raw, scope) => {
    const s = (raw || '').replace(/[x×]/gi, '*').trim();
    if (!s) return;
    // 'month' with an unchanged value is a no-op; 'global' with the SAME
    // value is meaningful — it pushes this amount to the recurring setup and
    // every month, which is exactly what the button promises.
    if (scope === 'month' && s === (item.calculation || String(item.amount))) return;
    try {
      const url = /[+*/-]/.test(s.slice(1))
        ? `/budget/items/${item.id}/set_calc/`
        : `/budget/items/${item.id}/set_amount/`;
      const body = /[+*/-]/.test(s.slice(1))
        ? { calculation: s, scope } : { amount: s, scope };
      const r = await api(url, { method: 'POST', body });
      onChange();
      // SAY it worked — a silent success reads as a dead button, especially
      // when the visible number doesn't change.
      setSaveNote(scope === 'global'
        ? `✓ Saved everywhere — ${r.updated} line${r.updated === 1 ? '' : 's'}`
          + `${r.recurring ? ' + the recurring setup' : ''}`
        : '✓ Saved for this month');
      setTimeout(() => setSaveNote(''), 3500);
    } catch (e) {
      ask({ title: 'Could not save',
            message: e.data?.detail || e.message, ok: 'OK' });
    }
  };

  // ±unit steppers on rate×count formulas: −1 unit = one unit paid off this
  // month (reduce_n), +1 undoes. The budget amount never shrinks — units paid
  // are handled in the summary maths, exactly like the private instance.
  // Inline row expand (mockup): tapping a row opens its editor in place.
  const acc = useAccordion();
  const [allRules, setAllRules] = useState([]);   // shop-event rules → EVENT DUE TODAY chip on collapsed rows
  useEffect(() => {
    let ok = true;
    api('/budget/place-visit-rules/').then((rs) => { if (ok) setAllRules(Array.isArray(rs) ? rs : (rs?.results || [])); }).catch(() => {});
    return () => { ok = false; };
  }, []);
  const expandId = (acc.openKey || '').startsWith('exp:') ? Number(acc.openKey.slice(4)) : null;
  // Receipt belongs to the LOGGED EVENT (Money Truth: a receipt button per line —
  // proof on the logged spend). Attach an image/PDF to a specific visit, else the
  // expense itself. Viewing an existing one stays in the Full record overlay.
  const scanReceipt = (item, visit) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*,application/pdf';
    input.onchange = async () => {
      const f = input.files && input.files[0]; if (!f) return;
      const fd = new FormData(); fd.append('receipt', f);
      try {
        await api(visit && visit.id ? `/budget/place-visits/${visit.id}/receipt/` : `/budget/items/${item.id}/receipt/`, { method: 'POST', form: fd });
        onChange && onChange();
      } catch { /* upload failed — offline/transient */ }
    };
    input.click();
  };
  const [inlineVisits, setInlineVisits] = useState([]);
  const [logVal, setLogVal] = useState('');
  const [totVal, setTotVal] = useState('');
  const advAmountRef = useRef(null);
  const advNoteRef = useRef(null);
  useEffect(() => {
    if (!expandId) { setInlineVisits([]); return; }
    setLogVal(''); setTotVal('');
    api(`/budget/place-visits/?item=${expandId}`)
      .then((vs) => setInlineVisits(Array.isArray(vs) ? vs : (vs?.results || [])))
      .catch(() => setInlineVisits([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandId, detailNonce]);
  const logSpend = async (item, raw) => {
    const amt = Number(String(raw).replace(',', '.'));
    if (!(amt > 0)) return;
    try { await api(`/budget/items/${item.id}/log/`, { method: 'POST', body: { amount: amt } }); }
    catch (e) { setError(e.data?.detail || e.message || 'Could not log the spend.'); }
    setLogVal(''); setDetailNonce((n) => n + 1); onChange();
  };
  const setTotal = async (item, raw) => {
    // Empty means "no change", never R0 (notes #621/#623): Number('') is 0.
    if (String(raw ?? '').trim() === '') return;
    const amt = Number(String(raw).replace(',', '.'));
    if (!(amt >= 0)) return;
    try { await api(`/budget/items/${item.id}/set_spent/`, { method: 'POST', body: { amount: amt } }); }
    catch (e) { setError(e.data?.detail || e.message || 'Could not set the total.'); }
    setTotVal(''); setDetailNonce((n) => n + 1); onChange();
  };
  const deleteInlineVisit = async (v) => {
    try { await api(`/budget/place-visits/${v.id}/`, { method: 'DELETE' }); }
    catch { /* ignore */ }
    setDetailNonce((n) => n + 1); onChange();
  };
  const stepUnits = async (item, delta) => {
    // ONE canonical primitive across every surface: /log/ (units:1) creates a
    // deletable unit visit AND bumps reduce_n under select_for_update; /unlog/
    // reverses the most recent one. Using it directly (instead of a place-visits
    // POST + bump) is why a recurring expense's +unit now works reliably, and we
    // surface any error instead of swallowing it so a dead tap is never silent.
    try {
      if (delta > 0) {
        await api(`/budget/items/${item.id}/log/`,
                  { method: 'POST', body: { units: 1, place: item.place || null } });
      } else {
        await api(`/budget/items/${item.id}/unlog/`, { method: 'POST', body: { kind: 'unit' } });
      }
    } catch (e) {
      setError(e.data?.detail || e.message || 'Could not update the unit — try again.');
    }
    setDetailNonce((n) => n + 1); onChange();
  };
  // Tap-to-type the exact paid-units count (sits between the − / + steppers).
  const setUnitsExact = async (item, count) => {
    // Set an exact paid-units count by LOGGING the difference through the canonical
    // primitive (never a bare reduce_n PATCH), so every change is a deletable visit
    // that shows under Logged (#292).
    const v = Math.max(0, Math.floor(Number(count)));
    const cur = Number(item.reduce_n) || 0;
    if (!(v >= 0) || v === cur) return;
    try {
      if (v > cur) {
        await api(`/budget/items/${item.id}/log/`,
                  { method: 'POST', body: { units: v - cur, place: item.place || null } });
      } else {
        for (let i = 0; i < cur - v; i++) {
          await api(`/budget/items/${item.id}/unlog/`, { method: 'POST', body: { kind: 'unit' } });
        }
      }
    } catch (e) { setError(e.data?.detail || e.message || 'Could not set the units.'); }
    setDetailNonce((n) => n + 1); onChange();
  };

  // Spent-so-far (actual): what really went out on this item. Freed difference
  // returns to the bank as leftover when you settle.
  const setSpent = async (item, value) => {
    await api(`/budget/items/${item.id}/`,
              { method: 'PATCH', body: { actual: value === '' ? null : value } });
    onChange();
  };

  // Mirror of the backend's spent maths so every row shows it live:
  // spent = used amount + (units paid × the formula's unit rate);
  // a ticked item counts its actual when entered, else the full budget.
  // MUST live BEFORE the return — renderRow (hoisted) closes over these.
  // unitRate + spentOf come from money.js — the ONE per-row money function
  // shared by rows, section headers and the detail card (Pass 3 item C).

  const tick = (item) => {
    // RECEIVING income is a moment, not a checkbox: confirm HOW MUCH landed
    // (it changes month to month) — and if it differs, offer to update the
    // recurring income for future months too.
    if (!item.completed && item.kind === 'income') {
      setReceiving(item);
      return;
    }
    // Ticking an expense with ANY tracking below its budget — logged spend,
    // formula units paid, or both — asks the settle question: use the full
    // amount, or the tracked figure with the difference back as leftover.
    const tracked = spentOf(item);
    if (!item.completed && item.kind === 'expense'
        && Number(item.amount) > 0 && tracked < Number(item.amount)) {
      setSettling(item);
      return;
    }
    complete(item, 'leftover');
  };

  return (
    <section className="mt-8">
      {/* #476 — Simple · Everything sits at the top of the This month list in both views. */}
      <div className="mb-4 max-w-xl"><BudgetViewSwitch id="sv-budget-switch" /></div>
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <h2 className="font-bold">This month</h2>
        <span className="text-xs text-slate-500 ml-auto">
          {expShown} of {expTotalScope} expense{expTotalScope === 1 ? '' : 's'} · {scoped ? scopeBankName : `all ${bankChips.length} bank${bankChips.length === 1 ? '' : 's'}`}
        </span>
        <button data-guide="move-money"
                className="text-xs text-slate-500 hover:text-vblue transition"
                title="Move money between your banks — records a transfer, not new income"
                onClick={() => setXferOpen((o) => !o)}>
          ⇄ Move money
        </button>
        <button data-guide="cash-spend" className="text-xs text-vblue hover:text-vblue/70 transition"
                title="Log a spend out of your cash (money spots) — reduces cash on hand"
                onClick={() => onCashSpend && onCashSpend()}>
          💵 Log a cash spend
        </button>
        <button className="text-xs text-slate-600 hover:text-red-400 transition"
                title="Delete every expense and income on this month (confirm first)"
                onClick={() => setClearing(true)}>
          Clear month…
        </button>
      </div>
      {/* Bank chips — scope both money columns at once (mockup). */}
      <div className="flex gap-1.5 flex-wrap mb-3">
        <button className={`text-xs px-2.5 py-1 rounded-full border ${
                  !bankFilter ? 'border-vblue bg-vblue/15' : 'border-edge text-slate-500'}`}
                onClick={() => setBankFilter(0)}>
          All banks
        </button>
        {bankChips.map(([id, name]) => (
          <button key={id}
                  className={`text-xs px-2.5 py-1 rounded-full border ${
                    bankFilter === id ? 'border-vblue bg-vblue/15' : 'border-edge text-slate-500'}`}
                  onClick={() => setBankFilter(bankFilter === id ? 0 : id)}>
            {name}
          </button>
        ))}
      </div>
      {/* Search + filter chips on ONE row (mockup): field + mic, then chips. */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-guide="month-search">
        <div className="flex-1 min-w-[16rem]">
          <SearchField value={q} onChange={setQ} onVoiceState={(s) => setMicOn(s === 'listening')}
                       placeholder="Search this month — name, category, place or formula…"
                       aiLine={ql ? `${visible.length} match${visible.length === 1 ? '' : 'es'}` : ''}
                       chips={q ? [{ key: 'q', label: `“${q}”`, onRemove: () => setQ('') }] : []} />
        </div>
        <div className="flex gap-1.5 flex-wrap items-center" data-guide="month-filters">
          {['Unpaid', 'Formula', 'Spend-only', 'Has a place'].map((f) => (
            <button key={f} type="button" onClick={() => setDfilter(dfilter === f ? '' : f)}
                    className={`text-xs px-3 min-h-[34px] rounded-full border transition ${dfilter === f ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-500 hover:border-slate-400'}`}>{f}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-vpurple mb-3 leading-relaxed">✨ {dAi}</p>
      {expShown === 0 && (dfilter || dqTrim) && (
        <div className="text-center py-6">
          <div className="text-3xl opacity-20 mb-1">🔒</div>
          <div className="text-sm text-slate-400">Nothing matches</div>
          <div className="text-xs text-slate-600 mt-1">
            {`Nothing in ${scoped ? scopeBankName : 'this month'}${dqTrim ? ` matches “${dqTrim}”` : ''}${dfilter ? ` under ${dfilter}` : ''}.`}
          </div>
          <button className="mt-3 text-xs px-3 py-1.5 rounded-full border border-vpurple/50 text-vpurple hover:bg-vpurple/10 transition"
                  onClick={() => { setQ(''); setDfilter(''); setBankFilter(0); }}>Clear the filters</button>
        </div>
      )}
      {xferOpen && (
        <div className="mb-3 p-3 rounded-xl border border-edge bg-white/5 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-400">Move</span>
          <input type="number" className="input !w-28 !py-1 text-sm" placeholder="Amount"
                 value={xferAmt} onChange={(e) => setXferAmt(e.target.value)} />
          <span className="text-xs text-slate-400">from</span>
          <select className="input !w-36 !py-1 text-sm" value={xferFrom}
                  onChange={(e) => setXferFrom(Number(e.target.value))}>
            <option value={0}>Select bank</option>
            {bankChips.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <span className="text-xs text-slate-400">to</span>
          <select className="input !w-36 !py-1 text-sm" value={xferTo}
                  onChange={(e) => setXferTo(Number(e.target.value))}>
            <option value={0}>Select bank</option>
            {bankChips.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <button className="text-xs px-3 py-1 rounded-full border border-vblue bg-vblue/15 text-vblue disabled:opacity-40"
                  disabled={!xferFrom || !xferTo || xferFrom === xferTo || !(Number(xferAmt) > 0)}
                  onClick={async () => {
                    try {
                      await api(`/budget/months/${budget.id}/move_funds/`, { method: 'POST', body: { from_bank: xferFrom, to_bank: xferTo, amount: Number(xferAmt) } });
                      setXferAmt(''); setXferFrom(0); setXferTo(0); setXferOpen(false); onChange();
                    } catch (e) { alert(e.data?.detail || e.message || 'Could not move the money.'); }
                  }}>
            Move
          </button>
          <span className="text-[10px] text-slate-500">Shows per-bank only, never as new income in All-banks.</span>
        </div>
      )}
      {/* Totals strip for the chosen view: the filtered bank's own income /
          expenses / leftover, or the GLOBAL numbers + the savings pot. */}
      {summary && (() => {
        const pb = bankFilter
          ? (summary.per_bank || []).find((b) => b.bank === bankFilter) : null;
        // Past savings live behind the pot chip: tap it to define what was
        // already banked before Vault (the baseline the pot grows from).
        const openPot = () => setPotOpen(true);
        const cells = pb
          ? [// One bank = its CURRENT STATE too: real balance, income RECEIVED
             // on it (with what is still coming), still to pay, and the actual
             // leftover now. Falls back to planned leftover only if the bank has
             // no known balance yet.
             ...(pb.has_actual ? [['Balance now', pb.actual, '']] : []),
             ['Income received', pb.received_income, 'text-emerald-400'],
             ...(Number(pb.income_coming) > 0.005
               ? [['Still coming', pb.income_coming, 'text-slate-400']] : []),
             ['Still to pay', pb.left_to_pay, ''],
             ...(pb.profit != null
               ? [['Leftover now', pb.profit,
                   Number(pb.profit) < 0 ? 'text-red-400' : 'text-emerald-400']]
               : [['Planned leftover', pb.remaining,
                   Number(pb.remaining) < 0 ? 'text-red-400' : 'text-emerald-400']]),
             ...(pb.bank_name === summary.savings_bank
               ? [['🐷 Savings pot', summary.savings_pot, 'text-vpurple', openPot]] : [])]
          : (() => {
              // ALL BANKS = the CURRENT STATE, not the plan: actual cash across
              // every bank, income actually RECEIVED (with what is still coming),
              // what is still to pay, and the ACTUAL leftover (= the hero number,
              // so they always agree and both move the moment you tick received).
              const pbs = summary.per_bank || [];
              const bal = pbs.filter((b) => b.has_actual)
                .reduce((a, b) => a + Number(b.actual || 0), 0);
              const stillToPay = pbs.reduce((a, b) => a + Number(b.left_to_pay || 0), 0);
              // Master-aware, from the backend — received + coming reconcile to
              // the month's real income and never double-count a split salary.
              const received = Number(summary.income_received || 0);
              const stillComing = Number(summary.income_coming || 0);
              return [
                ['Balance now', bal, ''],
                ['Income received', received, 'text-emerald-400'],
                ...(stillComing > 0.005
                  ? [['Still coming', stillComing, 'text-slate-400']] : []),
                ['Still to pay', stillToPay, ''],
                ...(Number(summary.to_savings) > 0
                  ? [['To savings', summary.to_savings, 'text-vpurple']] : []),
                ['Leftover', summary.leftover_actual,
                 Number(summary.leftover_actual) < 0 ? 'text-red-400' : 'text-emerald-400'],
                ['🐷 Savings pot', summary.savings_pot, 'text-vpurple', openPot],
              ];
            })();
        return (
          <div className="flex gap-2 flex-wrap mb-4">
            {cells.map(([label, val, cls, onClick]) => (
              <div key={label}
                   className={`border border-edge rounded-xl px-3 py-1.5 bg-ink/40 ${
                     onClick ? 'cursor-pointer hover:border-vpurple transition' : ''}`}
                   title={onClick ? 'Tap: deposits, withdrawals, this month, past savings' : undefined}
                   onClick={onClick}>
                <span className="text-[10px] uppercase tracking-wide text-slate-500 block">
                  {label}
                </span>
                <span className={`text-sm font-semibold ${cls}`}>{fmtR(val)}</span>
              </div>
            ))}
          </div>
        );
      })()}
      {clearing && (
        <div className="fixed inset-0 bg-black/70 grid place-items-center z-50 px-6"
             onClick={() => setClearing(false)}>
          <div className="card max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold">Clear {budget.month} completely?</h3>
            <p className="text-sm text-slate-400 mt-2">
              Every expense and income on this month is deleted — ticks and all.
              Bank balances, savings, and your recurring setup stay untouched;
              a cleared month stays empty (nothing respawns).
            </p>
            <div className="mt-4 flex gap-3">
              <button className="flex-1 border border-red-400/60 text-red-400 rounded-xl py-2.5
                                 hover:bg-red-400/10 transition"
                      onClick={async () => {
                        await api(`/budget/months/${budget.id}/clear_items/`, { method: 'POST' });
                        setClearing(false); onChange();
                      }}>
                Delete everything
              </button>
              <button className="flex-1 border border-edge rounded-xl py-2.5"
                      onClick={() => setClearing(false)}>
                Keep the month
              </button>
            </div>
          </div>
        </div>
      )}
      {detail && (() => {
        // Always render the FRESH item — edits reload the budget, and the
        // overlay must show the new truth, not the snapshot it opened with.
        const it = (budget.items || []).find((x) => x.id === detail.id) || detail;
        const sp = it.kind === 'expense' ? spentOf(it) : 0;
        const lf = it.kind === 'expense' ? Number(it.amount) - sp : 0;
        return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-[2px] grid place-items-center z-50 px-6"
             data-guide-context="item-detail"
             onClick={() => setDetail(null)}>
          <div className="card max-w-2xl w-full max-h-[88vh] overflow-y-auto !p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="text-2xl">{it.kind === 'income' ? '💰' : '🧾'}</span>
              <div className="flex-1">
                <h3 className="font-bold text-lg leading-tight">{it.title}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {it.kind === 'income' ? 'Income' : (it.category_name || 'Expense')}
                  {it.completed && (it.kind === 'income' ? ' · received ✓' : ' · paid ✓')}
                </p>
              </div>
              <button className="w-8 h-8 rounded-full border border-red-400/50 text-red-400
                                 hover:bg-red-400/10 transition shrink-0"
                      title="Delete this item"
                      onClick={() => { setDetail(null); setDeleting(it); }}>🗑</button>
              <button className="w-8 h-8 rounded-full border border-edge text-slate-400
                                 hover:text-white hover:border-slate-400 transition"
                      onClick={() => setDetail(null)}>✕</button>
            </div>

            {it.kind !== 'expense' && (<>
            {/* Amount or formula + explicit scope */}
            <label className="block text-xs text-slate-500 mt-5 mb-1">
              Amount — or a formula like 200*31
            </label>
            <div className="flex gap-1.5">
              <input id="detail-amount" className="input text-right font-medium flex-1"
                     key={`${it.id}:${it.calculation || it.amount}`}
                     defaultValue={it.calculation || it.amount}
                     onChange={(e) => setLiveCalc(e.target.value)} />
              {it.kind === 'expense' && (() => {
                // Several formula options counted on this month's real days and
                // weekdays (#67); a chip drops into the editable amount box and
                // saves for this month, and Undo puts the old value back.
                const fmBefore = it.calculation || String(it.amount ?? '');
                const dropIn = (f) => { const el = document.getElementById('detail-amount'); if (el) el.value = f; setLiveCalc(f); saveAmount(it, f, 'month'); };
                return (
                  <FormulaOptions endpoint="/budget/places/suggest_formula/" label="AI"
                    body={{ title: it.title, amount: it.amount, note: it.description, item: it.id, month: budget.month }}
                    onPick={dropIn}
                    onUndo={() => dropIn(fmBefore)} />
                );
              })()}
            </div>
            {(() => {
              // Live read-back: reflect what's IN THE BOX as you type, not the
              // last-saved value. Falls back to the saved formula/amount untouched.
              const src = (liveCalc !== '' ? liveCalc : (it.calculation || String(it.amount ?? '')))
                .replace(/[x×]/gi, '*').trim();
              const m = src.match(/^(\d+(?:\.\d+)?)\*(\d+(?:\.\d+)?)$/);
              if (m) {
                const x = +m[1], y = +m[2];
                return (
                  <>
                    <p className="text-[11px] text-slate-500 mt-1">= {fmtR(x * y)} today</p>
                    <p className="text-[11px] text-vblue mt-1">Reads as {Math.min(x, y)} × {moneySymbol()}{Math.max(x, y)} = {moneySymbol()}{(x * y).toFixed(2).replace(/\.00$/, '')} — each ± step pays {moneySymbol()}{Math.max(x, y)}.</p>
                  </>
                );
              }
              const n = Number(src);
              if (src !== '' && !Number.isNaN(n)) {
                return <p className="text-[11px] text-slate-500 mt-1">= {fmtR(n)} today</p>;
              }
              return null;
            })()}
            {(() => {
              // Formula budgets get − / + beside the amount: one tap bumps the
              // COUNT. The RATE is the larger factor (order-independent, matching
              // the read-back and the backend), the COUNT the smaller — bump the
              // smaller wherever it sits so 2*400 and 400*2 both step the "2".
              const cm = String(it.calculation || '').replace(/\s+/g, '')
                .match(/^([0-9.]+)\*([0-9.]+)(.*)$/);
              if (!cm) return null;
              const a = Number(cm[1]), b = Number(cm[2]);
              const rate = Math.max(a, b), count = Math.min(a, b);
              const countFirst = a <= b;
              const bumpCount = async (dd) => {
                const nc = Math.max(1, count + dd);
                const raw = countFirst ? `${nc}*${cm[2]}${cm[3] || ''}`
                                       : `${cm[1]}*${nc}${cm[3] || ''}`;
                let scope = 'month';
                if (it.template) {
                  scope = (await ask({
                    title: 'Apply the new count where?',
                    message: `Budget count → ${nc}× R${rate}. Just this month, `
                      + 'or the recurring setup and every month with this name too?',
                    ok: 'Everywhere', cancel: 'Just this month' }))
                    ? 'global' : 'month';
                }
                saveAmount(it, raw, scope);
              };
              return (
                <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
                  <span>budget count:</span>
                  <button className="w-7 h-7 rounded-full border border-edge text-sm
                                     leading-none hover:border-vblue hover:text-vblue
                                     disabled:opacity-30"
                          title="One unit fewer in the budget"
                          disabled={count <= 1}
                          onClick={() => bumpCount(-1)}>−</button>
                  <span className="text-slate-300 min-w-[44px] text-center">
                    {count}× {moneySymbol()}{rate}
                  </span>
                  <button className="w-7 h-7 rounded-full border border-edge text-sm
                                     leading-none hover:border-vblue hover:text-vblue"
                          title="One unit more in the budget"
                          onClick={() => bumpCount(+1)}>+</button>
                </div>
              );
            })()}
            <div className="flex gap-2 mt-2">
              <button className="btn-primary !py-1.5 text-sm flex-1"
                      onClick={() => saveAmount(it, document.getElementById('detail-amount').value, 'month')}>
                Save · this month
              </button>
              <button className="flex-1 py-1.5 rounded-xl border border-edge text-sm text-slate-300
                                 hover:border-vpurple transition"
                      title="Recurring setup + every month with this name"
                      onClick={async () => {
                        const v = document.getElementById('detail-amount').value;
                        if (await ask({
                          title: `Apply “${it.title}” everywhere?`,
                          message: 'This sets it to this amount on the recurring '
                            + 'setup — so FUTURE months inherit it — AND every '
                            + 'CURRENT month with this name. Continue?',
                          ok: 'Yes — everywhere', cancel: 'Cancel' })) {
                          saveAmount(it, v, 'global');
                        }
                      }}>
                Save · everywhere
              </button>
            </div>
            {saveNote && (
              <p className="text-xs text-emerald-400 mt-1.5">{saveNote}</p>
            )}

            {/* ② Shop-event formula — cadence + what each visit logs, editable
                inline (mirrors the Spend Map's Advanced editor) so map and
                dashboard stay identical. Only shows when a standing event exists. */}
            {it.kind === 'expense' && (() => {
              const evtRule = detailRules.find((r) =>
                (r.item_title || '').toLowerCase() === (it.title || '').toLowerCase());
              if (!evtRule) return null;
              const evtPlace = detailPlaces.find((p) => p.id === evtRule.place) || null;
              const rate = unitRate(it.calculation) || 0;
              const evUnits = Number(evtRule.units) || 0;
              const byUnits = rate > 0 && evUnits > 0;
              const perUnit = (byUnits && Number(evtRule.amount) > 0) ? Number(evtRule.amount) : rate;
              const times = evtRule.cadence === 'daily' ? Math.max(1, Number(evtRule.times_per_day) || 1) : 1;
              const per = byUnits ? evUnits * perUnit : (Number(evtRule.amount) || 0);
              const when = evtRule.cadence === 'monthly' ? ` on day ${evtRule.day_of_month || 1}`
                : evtRule.cadence === 'geo' ? ' when you’re there' : '';
              const patchRule = async (body) => {
                const up = await api(`/budget/place-visit-rules/${evtRule.id}/`,
                  { method: 'PATCH', body }).catch(() => null);
                if (up) setDetailRules((rs) => rs.map((x) => (x.id === evtRule.id ? up : x)));
                setDetailNonce((n) => n + 1); onChange();
              };
              return (
                <div className="mt-4 border border-amber-400/40 rounded-xl p-3 grid gap-2">
                  <div className="text-[10px] uppercase tracking-wide text-amber-400 min-w-0 truncate">
                    ② Shop-event formula{evtPlace ? ` · ${evtPlace.name}` : ''}
                  </div>

                  <div className="text-[10px] uppercase tracking-wide text-slate-500">How often</div>
                  <div className="flex gap-1.5">
                    {[['daily', 'Every day'], ['monthly', 'Monthly'], ['geo', 'When there']].map(([k, l]) => (
                      <button key={k} type="button"
                              className={`flex-1 text-[11px] py-1.5 rounded-lg border ${
                                evtRule.cadence === k ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                                                      : 'border-edge text-slate-500'}`}
                              onClick={() => evtRule.cadence !== k && patchRule({ cadence: k })}>{l}</button>
                    ))}
                  </div>
                  {evtRule.cadence === 'daily' && (
                    <label className="text-[10px] text-slate-500">Times a day
                      <input className="input !py-1.5 text-sm w-full mt-0.5" type="number" min="1" max="12"
                             key={`tpd${evtRule.id}:${evtRule.times_per_day}`} defaultValue={evtRule.times_per_day || 1}
                             onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                             onBlur={(e) => { const v = Math.max(1, Number(e.target.value) || 1);
                               if (v !== (Number(evtRule.times_per_day) || 1)) patchRule({ times_per_day: v }); }} />
                    </label>
                  )}
                  {evtRule.cadence === 'monthly' && (
                    <label className="text-[10px] text-slate-500">Day of month (1–28)
                      <input className="input !py-1.5 text-sm w-full mt-0.5" type="number" min="1" max="28"
                             key={`dom${evtRule.id}:${evtRule.day_of_month}`} defaultValue={evtRule.day_of_month || 1}
                             onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                             onBlur={(e) => { const v = Math.min(28, Math.max(1, Number(e.target.value) || 1));
                               if (v !== (Number(evtRule.day_of_month) || 1)) patchRule({ day_of_month: v }); }} />
                    </label>
                  )}

                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">What each visit logs</div>
                  {rate > 0 && (
                    <div className="flex gap-1.5">
                      {[['units', `Items · ${moneySymbol()}${rate} each`], ['money', `Money (${moneySymbol()})`]].map(([m, l]) => {
                        const active = (m === 'units') === byUnits;
                        return (
                          <button key={m} type="button"
                                  className={`flex-1 text-[11px] py-1.5 rounded-lg border ${
                                    active ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                                            : 'border-edge text-slate-500'}`}
                                  onClick={() => { if (active) return;
                                    if (m === 'units') patchRule({ units: evUnits || 1, amount: 0 });
                                    else patchRule({ units: 0, amount: Number(evtRule.amount) || rate }); }}>{l}</button>
                        );
                      })}
                    </div>
                  )}
                  {byUnits ? (
                    <>
                      <label className="text-[10px] text-slate-500">Items (units) per visit
                        <input className="input !py-1.5 text-sm w-full mt-0.5" type="number" min="0"
                               key={`eu${evtRule.id}:${evtRule.units}`} defaultValue={evtRule.units}
                               onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                               onBlur={(e) => { const v = Number(e.target.value) || 0;
                                 if (v !== evUnits) patchRule({ units: v }); }} />
                      </label>
                      <label className="text-[10px] text-slate-500">Amount per item ({moneySymbol()}) — blank uses {moneySymbol()}{rate}
                        <input className="input !py-1.5 text-sm w-full mt-0.5" inputMode="decimal"
                               key={`ea${evtRule.id}:${evtRule.amount}`} placeholder={`R${rate} (formula default)`}
                               defaultValue={Number(evtRule.amount) > 0 ? evtRule.amount : ''}
                               onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                               onBlur={(e) => { const v = e.target.value.trim(); patchRule({ amount: v === '' ? 0 : v }); }} />
                      </label>
                    </>
                  ) : (
                    <label className="text-[10px] text-slate-500">Money per visit ({moneySymbol()})
                      <input className="input !py-1.5 text-sm w-full mt-0.5" inputMode="decimal"
                             key={`em${evtRule.id}:${evtRule.amount}`} defaultValue={evtRule.amount}
                             onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                             onBlur={(e) => { const v = e.target.value.trim();
                               if (v !== String(evtRule.amount)) patchRule({ units: 0, amount: v === '' ? 0 : v }); }} />
                    </label>
                  )}
                  {per > 0 && (
                    <p className="text-[11px] text-emerald-400">
                      Logs {byUnits ? `${evUnits}× ${fmtR(perUnit)} = ${fmtR(per)}` : fmtR(per)}
                      {times > 1 ? ` × ${times}/day = ${fmtR(per * times)}/day` : when}{times > 1 ? when : ''}.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Bank + category + salary flag — every home is changeable. */}
            <div className="flex gap-2 mt-4 items-center flex-wrap">
              {it.kind === 'expense' && (
                <CategoryPicker categories={categories} value={it.category}
                                title={it.title} kind={it.kind}
                                className="flex-1 min-w-[160px]"
                                key={`cat${it.id}:${it.category ?? ''}`}
                                onPicked={async (id) => {
                                  await api(`/budget/items/${it.id}/`,
                                            { method: 'PATCH',
                                              body: { category: id } });
                                  onChange();
                                }} />
              )}
              <select className="input !w-auto flex-1 text-sm" value={it.bank || ''}
                      onChange={async (e) => {
                        await api(`/budget/items/${it.id}/`,
                                  { method: 'PATCH',
                                    body: { bank: e.target.value ? Number(e.target.value) : null } });
                        onChange();
                      }}>
                <option value="">no bank</option>
                {banks.map((b) => <option key={b.id} value={b.id}>{b.account_name ? `${b.name} \u00b7 ${b.account_name}` : b.name}</option>)}
              </select>
              {it.kind === 'income' && (
                <button className={`text-[11px] uppercase tracking-wide rounded-lg px-2.5 py-2 border ${
                          it.is_salary ? 'border-vpurple text-vpurple' : 'border-edge text-slate-500'}`}
                        title="The salary that starts your new month (salary mode)"
                        onClick={async () => {
                          await api(`/budget/items/${it.id}/`,
                                    { method: 'PATCH', body: { is_salary: !it.is_salary } });
                          onChange();
                        }}>
                  {it.is_salary ? 'salary ✓' : 'salary?'}
                </button>
              )}
            </div>

            {/* Any line can join the recurring setup AFTER the fact — the
                July one-off becomes every month's, seeded ahead, deletable
                per month as always. */}
            {!it.template && (
              <button className="w-full mt-2 text-sm py-2 rounded-xl border border-vpurple/60
                                 text-vpurple hover:bg-vpurple/10 transition"
                      title="Creates its recurring definition and seeds it into every prepared month"
                      onClick={async () => {
                        try {
                          await api(`/budget/items/${it.id}/make_recurring/`,
                                    { method: 'POST' });
                          onChange();
                          setSaveNote('✓ Recurring — seeded into every month');
                          setTimeout(() => setSaveNote(''), 3500);
                        } catch (e) {
                          ask({ title: 'Could not make it recurring',
                                message: e.data?.detail || e.message, ok: 'OK' });
                        }
                      }}>
                ↻ Every month from now — add to recurring
              </button>
            )}

            {/* An UNRECEIVED income's master choice happens at receive time —
                give the row's overlay a straight door to that card. */}
            {it.kind === 'income' && !it.completed && (
              <button className="w-full mt-2 text-sm py-2 rounded-xl border border-vblue/60
                                 text-vblue hover:bg-vblue/10 transition"
                      title="Asks the landed amount, the landing bank, and whether to deduct the ★ master"
                      onClick={() => { setDetail(null); setReceiving(it); }}>
                Receive now… (amount · landing bank · deduct from ★ master)
              </button>
            )}

            {/* Retroactive master pairing: any RECEIVED income can flip
                whether its money came from the ★ master — applied or
                reversed on the master's balance the moment you tap. */}
            {it.kind === 'income' && it.completed && !it.salary_primary
              && (budget.items || []).some((x) => x.salary_primary) && (
              <button className={`w-full mt-2 text-sm py-2 rounded-xl border transition ${
                        it.master_deducted
                          ? 'border-amber-400 text-amber-400 bg-amber-400/10'
                          : 'border-edge text-slate-400 hover:border-amber-400'}`}
                      title="Money moved from the master deducts its bank; new money leaves it alone"
                      onClick={async () => {
                        try {
                          await api(`/budget/items/${it.id}/master_deduction/`,
                                    { method: 'POST',
                                      body: { on: !it.master_deducted } });
                          onChange();
                        } catch (e) {
                          ask({ title: 'Could not change the pairing',
                                message: e.data?.detail || e.message, ok: 'OK' });
                        }
                      }}>
                {it.master_deducted
                  ? '✓ Deducted from ★ master — tap to undo (it was new money)'
                  : 'Deduct from ★ master — this money moved from there'}
              </button>
            )}

            </>)}

            {it.kind !== 'expense' && (<>
            {/* Note + receipt */}
            <label className="block text-xs text-slate-500 mt-4 mb-1">
              Note — yours or AI's, edit freely
            </label>
            <textarea className="input min-h-[70px]" defaultValue={it.description}
                      onBlur={async (e) => {
                        if (e.target.value !== it.description) {
                          await api(`/budget/items/${it.id}/`,
                                    { method: 'PATCH', body: { description: e.target.value } });
                          onChange();
                        }
                      }} />
            {it.kind === 'expense' && (<>
              <label className="block text-xs text-slate-500 mt-4 mb-1">Where it's spent — shop or address</label>
              {it.place ? (() => {
                const linked = detailPlaces.find((pl) => pl.id === it.place) || {};
                return (
                  <>
                    <div className="flex items-center gap-2 min-w-0 border border-edge rounded-xl px-2.5 py-1.5 text-sm">
                      <span className="flex-1 min-w-0 truncate">📍 {linked.name || 'a place'}</span>
                      <button className="text-[11px] text-red-400/80 hover:text-red-400"
                              onClick={async () => { await api(`/budget/items/${it.id}/`, { method: 'PATCH', body: { place: null } }); onChange(); }}>unlink</button>
                    </div>
                    {/* The pin on a mini-map — WHERE it lands, right in the overlay. */}
                    {linked.lat != null && linked.lng != null && (
                      <PinPreview lat={linked.lat} lng={linked.lng} height={130} />
                    )}
                  </>
                );
              })() : (
                <p className="text-[11px] text-slate-500 mb-1">Not linked yet — search to add a shop or address.</p>
              )}
              <input className="input flex-1 !py-1.5 text-sm mt-1.5 w-full" placeholder="🔎 Shop name or address — searches as you type"
                     value={linkQ} onChange={(e) => setLinkQ(e.target.value)} />
              {linkQ.trim().length >= 2 && (() => {
                // YOUR saved places (on record) that match come FIRST; then new
                // shops/addresses resolved from the map, deduped by name.
                const q = linkQ.trim().toLowerCase();
                const saved = detailPlaces.filter((p) =>
                  `${p.name || ''} ${p.address || ''}`.toLowerCase().includes(q)).slice(0, 5);
                const savedNames = new Set(saved.map((p) => (p.name || '').toLowerCase()));
                const fresh = linkResults.filter((c) => !savedNames.has((c.name || '').toLowerCase()));
                return (
                  <div className="grid gap-1 mt-1.5">
                    {saved.map((p) => (
                      <button key={`s${p.id}`} type="button"
                              className="text-left border border-vblue/40 bg-vblue/5 rounded-lg px-2.5 py-1.5 text-sm hover:border-vblue"
                              onClick={() => setLinkPreview({ id: p.id, name: p.name, address: p.address, lat: p.lat, lng: p.lng })}>
                        📍 {p.name}<span className="text-[11px] text-vblue"> · on record</span>
                        {p.address ? <span className="text-[11px] text-slate-500"> · {p.address}</span> : null}
                      </button>
                    ))}
                    {fresh.slice(0, 6).map((c, i) => (
                      <button key={`r${i}`} type="button"
                              className="text-left border border-edge rounded-lg px-2.5 py-1.5 text-sm hover:border-vblue"
                              onClick={() => setLinkPreview({ name: c.name, address: c.address, lat: c.lat, lng: c.lng, source: c.source })}>
                        📍 {c.name}<span className="text-[11px] text-slate-500"> · {c.address}</span>
                      </button>
                    ))}
                    {!saved.length && !fresh.length && (
                      <p className="text-[11px] text-slate-500">Searching…</p>
                    )}
                  </div>
                );
              })()}
              {/* Picked a place → SHOW THE PIN on a mini-map before committing, then
                  confirm to link it (create the place first if it's a new one). */}
              {linkPreview && (
                <div className="mt-2 border border-vblue/40 rounded-xl p-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="flex-1 min-w-0 truncate">📍 {linkPreview.name}
                      {linkPreview.address ? <span className="text-[11px] text-slate-500"> · {linkPreview.address}</span> : null}
                    </span>
                    <button className="text-[11px] text-slate-500 hover:text-white" onClick={() => setLinkPreview(null)}>✕</button>
                  </div>
                  {linkPreview.lat != null && linkPreview.lng != null && (
                    <PinPreview lat={linkPreview.lat} lng={linkPreview.lng} height={130} />
                  )}
                  <button className="btn-primary w-full !py-1.5 text-sm mt-2"
                          onClick={async () => {
                            let placeId = linkPreview.id;
                            if (!placeId) {
                              const pl = await api('/budget/places/', { method: 'POST', body: {
                                name: linkPreview.name, address: linkPreview.address || '',
                                lat: linkPreview.lat, lng: linkPreview.lng,
                                kind: linkPreview.source === 'address' ? 'address' : 'shop' } }).catch(() => null);
                              placeId = pl && pl.id;
                            }
                            if (placeId) {
                              await api(`/budget/items/${it.id}/`, { method: 'PATCH', body: { place: placeId } });
                              setLinkQ(''); setLinkResults([]); setLinkPreview(null); onChange();
                            }
                          }}>
                    Link {it.title} here
                  </button>
                </div>
              )}
            </>)}
            {/* Receipts are an EXPENSE thing — income has no till slip. Tap opens
                the one shared overlay (image, ✨ re-read, apply total, detach). */}
            {it.kind === 'expense' && it.receipt && (
              <button type="button"
                      className="mt-3 w-full rounded-xl border border-edge px-3 py-2 text-sm text-vblue hover:border-vblue transition min-h-[44px]"
                      onClick={() => setReceiptOpen({ src: `/budget/items/${it.id}/receipt/`, actionBase: `/budget/items/${it.id}`, attachedTo: it.title, applyItem: it })}>
                🧾 View receipt
              </button>
            )}
            {it.kind === 'expense' && (
              <div className="mt-4 border-t border-edge pt-3">
                {!mergeOpen ? (
                  <button className="text-xs text-slate-500 hover:text-vpurple transition"
                          onClick={() => { setMergeOpen(true); setMergeQ(''); }}>
                    🔀 Make this a spend on another expense…
                  </button>
                ) : (
                  <div className="grid gap-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wide text-vpurple flex-1 min-w-0 truncate">
                        Log “{it.title}” as a spend on…
                      </span>
                      <button className="text-[11px] text-slate-500 hover:text-white shrink-0"
                              onClick={() => setMergeOpen(false)}>cancel</button>
                    </div>
                    <input className="input !py-1.5 text-sm" placeholder="🔎 Find the expense to fold it into…"
                           value={mergeQ} onChange={(e) => setMergeQ(e.target.value)} />
                    <div className="max-h-40 overflow-y-auto grid gap-1">
                      {(budget.items || [])
                        .filter((x) => x.kind === 'expense' && x.id !== it.id && x.source !== 'transfer'
                          && (!mergeQ.trim() || (x.title || '').toLowerCase().includes(mergeQ.trim().toLowerCase())))
                        .slice(0, 30)
                        .map((x) => (
                          <button key={x.id} type="button"
                                  className="text-left border border-edge rounded-lg px-2.5 py-1.5 text-sm hover:border-vpurple transition flex items-center gap-2 min-w-0"
                                  onClick={async () => {
                                    const plan = await api(`/budget/items/${it.id}/merge_into/`,
                                      { method: 'POST', body: { target: x.id, dry: 1 } }).catch(() => null);
                                    if (!plan) return;
                                    const ok = await ask({
                                      title: 'Make it a spend?',
                                      message: `Log ${fmtR(plan.value)} as a spend on “${x.title}” and remove “${it.title}”. This can't be undone.`,
                                      ok: 'Do it', cancel: 'Cancel' });
                                    if (!ok) return;
                                    await api(`/budget/items/${it.id}/merge_into/`,
                                      { method: 'POST', body: { target: x.id } }).catch(() => {});
                                    setMergeOpen(false); setDetail(null); onChange();
                                  }}>
                            <span className="flex-1 min-w-0 truncate">{x.title}</span>
                            <span className="text-[11px] text-slate-500 shrink-0">{fmtR(x.actual ?? x.amount)}</span>
                          </button>
                        ))}
                    </div>
                    <p className="text-[10px] text-slate-600">
                      Its value logs as one spend on the chosen expense (at this one's location, noted with its name); this expense is then removed.
                    </p>
                  </div>
                )}
              </div>
            )}
            {receiptBusy && (
              <p className="text-xs text-vblue mt-3 animate-pulse">🧾 Reading your receipt with AI…</p>
            )}
            <div className="flex items-center justify-between mt-3">
              {it.kind === 'expense' ? (
                <div className="flex flex-wrap gap-2">
                  <label className="inline-block border border-edge rounded-xl px-3 py-2 text-sm cursor-pointer hover:border-vblue transition">
                    📷 {it.receipt ? 'Replace · camera' : 'Camera'}
                    <input type="file" accept="image/*" capture="environment" className="hidden" disabled={receiptBusy}
                           onChange={(e) => { const f = e.target.files?.[0]; if (f) attachAndInterpret(it, f); }} />
                  </label>
                  <label className="inline-block border border-edge rounded-xl px-3 py-2 text-sm cursor-pointer hover:border-vblue transition">
                    📎 {it.receipt ? 'Replace · upload' : 'Upload (image or PDF)'}
                    <input type="file" accept="image/*,application/pdf" className="hidden" disabled={receiptBusy}
                           onChange={(e) => { const f = e.target.files?.[0]; if (f) attachAndInterpret(it, f); }} />
                  </label>
                </div>
              ) : <span />}
              <button className="text-sm text-slate-600 hover:text-red-400 transition"
                      onClick={() => { setDetail(null); setDeleting(it); }}>
                Delete…
              </button>
            </div>
            </>)}
          </div>
        </div>
        );
      })()}
      <ReceiptOverlay
        open={!!receiptOpen}
        onClose={() => setReceiptOpen(null)}
        src={receiptOpen?.src}
        attachedTo={receiptOpen?.attachedTo}
        onReread={receiptOpen ? (() => api(`${receiptOpen.actionBase}/reread_receipt/`, { method: 'POST' })) : undefined}
        onDetach={receiptOpen ? (async () => {
          await api(`${receiptOpen.actionBase}/detach_receipt/`, { method: 'POST' });
          setDetailNonce((n) => n + 1);
          if (receiptOpen.applyItem) setDetail((d) => (d ? { ...d, receipt: null } : d));
          onChange();
        }) : undefined}
        onApplyTotal={receiptOpen?.applyItem ? (async (v) => {
          // Applying the receipt total logs a reversible spend through the one
          // canonical /log/ primitive — deletable like any other entry.
          await api(`/budget/items/${receiptOpen.applyItem.id}/log/`, { method: 'POST', body: { amount: v } });
          setDetailNonce((n) => n + 1); onChange();
        }) : undefined}
      />
      {markDoneAsk && (
        <div className="fixed inset-0 bg-black/70 grid place-items-center z-50 px-6"
             onClick={() => setMarkDoneAsk(null)}>
          <div className="card max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold">Mark "{markDoneAsk.title}" as done?</h3>
            <p className="text-sm text-slate-400 mt-1">
              Receipt saved and read. This expense isn't marked paid yet — mark it done now?
              Any unspent budget frees back to its bank.
            </p>
            <div className="flex gap-2 mt-4">
              <button className="btn-primary flex-1"
                      onClick={async () => { const it0 = markDoneAsk; setMarkDoneAsk(null); await complete(it0, 'free'); }}>
                Mark as done
              </button>
              <button className="flex-1 border border-edge rounded-xl py-2 text-slate-300 hover:border-vblue transition"
                      onClick={() => setMarkDoneAsk(null)}>
                Not yet
              </button>
            </div>
          </div>
        </div>
      )}
      {deleting && (
        <div className="fixed inset-0 bg-black/70 grid place-items-center z-50 px-6"
             onClick={() => setDeleting(null)}>
          <div className="card max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold">Delete "{deleting.title}"?</h3>
            {(() => {
              // A one-off has nothing to decide — just remove it. A RECURRING
              // expense asks how far it goes: this month only, the prepared
              // months ahead too, or everywhere incl. the recurring itself.
              const isRec = !!deleting.template;
              const del = async (scope) => {
                await api(`/budget/items/${deleting.id}/?scope=${scope}`,
                          { method: 'DELETE' });
                setDeleting(null); onChange();
              };
              if (!isRec) {
                return (
                  <>
                    <p className="text-sm text-slate-400 mt-2">
                      Removes it from {budget.month}. This can't be undone.
                    </p>
                    <div className="grid gap-2 mt-4">
                      <button className="border border-red-400/60 text-red-400 rounded-xl py-2.5
                                         text-sm hover:bg-red-400/10 transition"
                              onClick={() => del('month')}>
                        Delete it
                      </button>
                      <button className="border border-edge rounded-xl py-2.5 text-sm"
                              onClick={() => setDeleting(null)}>
                        Cancel
                      </button>
                    </div>
                  </>
                );
              }
              return (
                <>
                  <p className="text-sm text-slate-400 mt-2">
                    This one recurs — delete just {budget.month}, or the months
                    ahead too? This can't be undone.
                  </p>
                  <div className="grid gap-2 mt-4">
                    <button className="border border-red-400/60 text-red-400 rounded-xl py-2.5
                                       text-sm hover:bg-red-400/10 transition"
                            onClick={() => del('month')}>
                      Only {budget.month}
                      <span className="block text-[11px] text-slate-500">
                        just this month — recurring setup and other months untouched
                      </span>
                    </button>
                    <button className="border border-red-400/60 text-red-400 rounded-xl py-2.5
                                       text-sm hover:bg-red-400/10 transition"
                            onClick={() => del('future')}>
                      {budget.month} + months ahead
                      <span className="block text-[11px] text-slate-500">
                        clears the prepared future months — recurring setup stays, ↻ Sync can bring it back
                      </span>
                    </button>
                    <button className="border border-red-400/60 text-red-400 rounded-xl py-2.5
                                       text-sm hover:bg-red-400/10 transition"
                            onClick={() => del('all')}>
                      Everywhere + stop recurring
                      <span className="block text-[11px] text-slate-500">
                        months ahead cleared and the recurring definition removed for good
                      </span>
                    </button>
                    <button className="border border-edge rounded-xl py-2.5 text-sm"
                            onClick={() => setDeleting(null)}>
                      Cancel
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {receiving && (
        <ReceiveIncome item={receiving} banks={banks}
                       masterBank={(budget.items || []).find((i) => i.salary_primary) || null}
                       onDone={() => { setReceiving(null); onChange(); }}
                       onCancel={() => setReceiving(null)} />
      )}
      {potOpen && (
        <SavingsPot budget={budget} banks={banks}
                    onClose={() => setPotOpen(false)} onChange={onChange} />
      )}
      {settling && (
        <div className="fixed inset-0 bg-black/70 grid place-items-center z-50 px-6"
             onClick={() => setSettling(null)}>
          <div className="card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold">
              {settling.title}: tracked {fmtR(spentOf(settling))} of {fmtR(settling.amount)}
            </h3>
            <p className="text-sm text-slate-400 mt-2">
              Tracked = spent so far plus formula units paid
              {settling.reduce_n > 0 && ` (${settling.reduce_n}× units)`}.
              What happens to the {fmtR(Number(settling.amount) - spentOf(settling))}{' '}
              you didn't use?
            </p>
            <div className="mt-4 grid gap-2">
              <button className="btn-primary" onClick={() => complete(settling, 'leftover')}>
                Settle at the tracked {fmtR(spentOf(settling))}
                <span className="block text-xs opacity-80">
                  {fmtR(Number(settling.amount) - spentOf(settling))} stays as leftover
                  in {settling.bank_name || 'its bank'}
                </span>
              </button>
              <button className="border border-edge rounded-xl py-3 hover:border-slate-500 transition"
                      onClick={() => complete(settling, 'full')}>
                Paid in full — the whole {fmtR(settling.amount)} is gone
              </button>
            </div>
            <button className="mt-3 text-sm text-slate-500 hover:text-slate-300"
                    onClick={() => setSettling(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {bankFilter > 0 && !visible.length && (
        <p className="text-slate-500 text-sm py-4">
          Nothing in {bankChips.find(([id]) => id === bankFilter)?.[1]} this month yet —
          use the + to log something into it, or the bank picker on any item to move one here.
        </p>
      )}
      {/* Money in / Money out — auto-fit columns (no media query); when two
          columns fit, a container query flips Money out to the LEFT and Money
          in to the RIGHT, so the single-column phone still reads income first. */}
      <style>{`@container vmoney (min-width:938px){.vmoney-in{order:2}.vmoney-out{order:1}}`}</style>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(460px,100%),1fr))', gap: '18px', alignItems: 'start', containerType: 'inline-size', containerName: 'vmoney' }}>
      {groups['Income'] && (() => {
        const inTotal = groups['Income'].reduce((a, i) => a + Number(i.amount || 0), 0);
        return (
          <div className="vmoney-in">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 flex items-center gap-2">
                <span className="text-emerald-400">▲</span> Money in
              </h3>
              <span className="text-xs font-bold text-emerald-400 border border-emerald-400/35 bg-emerald-400/10 rounded-full px-2.5 py-0.5">{fmtR(inTotal)}</span>
            </div>
            <div className="space-y-2">
              {groups['Income'].map((i) => (
                <div key={i.id} className="card !p-0 overflow-hidden">{renderRow(i)}</div>
              ))}
            </div>
          </div>
        );
      })()}
      {(() => {
        const outGroups = names.filter((g) => groups[g] && g !== 'Income');
        if (!outGroups.length) return null;
        const outExp = outGroups.flatMap((g) => groups[g]).filter((i) => i.kind === 'expense');
        const outBudget = outExp.reduce((a, i) => a + Number(i.amount || 0), 0);
        const outSpent = outExp.reduce((a, i) => a + Number(spentOf(i) || 0), 0);
        return (
          <div className="vmoney-out">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 flex items-center gap-2">
                <span className="text-red-400">▼</span> Money out
              </h3>
              <span className="text-xs font-bold text-slate-300 border border-edge rounded-full px-2.5 py-0.5">{fmtR(outSpent)} of {fmtR(outBudget)}</span>
            </div>
            <div className="space-y-2">
              {outGroups.flatMap((g) => groups[g]).map((i) => (
                <div key={i.id} className="card !p-0 overflow-hidden">{renderRow(i)}</div>
              ))}
            </div>
          </div>
        );
      })()}
      </div>
    </section>
  );

  // One row, used by both columns. STUNNING-BUT-CALM: the row only SHOWS —
  // tick to pay/receive, everything else reads at a glance; clicking the row
  // opens the detail overlay where ALL editing lives (amount/formula, bank,
  // spent, units, note, receipt, delete). One gesture, one place.
  function renderRow(i) {
    // Transfer notes: slim, calm, informational — the arrow tells the story,
    // the amount lives in the title, nothing to tick.
    if (i.source === 'transfer') {
      return (
        <div key={i.id}
             className="flex items-center gap-3 px-4 py-2.5 cursor-pointer
                        hover:bg-white/[0.03] transition text-slate-400"
             title={i.description}
             onClick={() => setDetail(i)}>
          <span className="text-sm">↔</span>
          <span className="flex-1 text-sm truncate">{i.title}</span>
          <span className="text-[10px] uppercase tracking-wide text-slate-600">transfer</span>
        </div>
      );
    }
    const spent = i.kind === 'expense' ? spentOf(i) : 0;
    const left = i.kind === 'expense' ? Number(i.amount) - spent : 0;
    // TIER 1 (Pass 5): the collapsed row shows STATE ONLY, never setup. Bank,
    // category, place and formula move into the expansion's DetailCard
    // ("Set up as … / Change"), so the sub-line reads where it STANDS.
    const leftClamped = Math.max(0, left);
    const over = i.kind === 'expense' && spent > Number(i.amount);
    // At most one attention chip (Pass 5 #1): OVER wins, else EVENT DUE TODAY when
    // an active shop-event for this expense (matched by title or place) fires today.
    const dueToday = i.kind === 'expense' && !over && !i.completed && allRules.some((r) => r.active
      && ((r.item_title || '').toLowerCase() === (i.title || '').toLowerCase() || String(r.place) === String(i.place))
      && (r.cadence === 'daily' || r.cadence === 'geo' || (r.cadence === 'monthly' && new Date().getDate() === (r.day_of_month || 1))));
    // #772 — a ticked expense still says what it cost and what it left over (or
    // went over), and an open one names where its spend came from underneath.
    const overBy = over ? fmtR(spent - Number(i.amount)) : null;
    const stateLine = i.kind === 'income'
      ? (i.completed ? 'received' : 'not received yet')
      : (i.completed
          ? (overBy ? `paid · ${fmtR(spent)} spent · ${overBy} over`
             : (leftClamped > 0 ? `paid · ${fmtR(spent)} spent · ${fmtR(leftClamped)} left over`
                : `paid in full · ${fmtR(spent)}`))
          : `${fmtR(spent)} of ${fmtR(i.amount)} · ${overBy ? overBy + ' over' : fmtR(leftClamped) + ' left'}`);
    const breakdown = i.kind === 'expense' ? spendBreakdown(i) : '';
    // Bank-local income in the All-banks view: visible for managing (open,
    // re-pair, delete-with-refund) but dimmed — it counts in its bank only.
    const localInc = !bankFilter && bankLocalIncome(i);
    const isOpen = expandId === i.id;
    return (
      <div key={i.id}>
      <div
           className={`flex items-center gap-3 px-4 py-3 cursor-pointer
                      hover:bg-white/[0.03] transition group ${
                        localInc ? 'opacity-60' : ''} ${isOpen ? 'bg-white/[0.02]' : ''}`}
           title="Tap to open" data-guide="expense-row" data-item-id={i.id}
           onClick={() => acc.toggle('exp:' + i.id)}>
        {/* Category icon tile (mockup lead). */}
        <span className="w-8 h-8 shrink-0 rounded-lg bg-surface2 border border-edge grid place-items-center text-base">
          {rowIcon(i)}
        </span>
        {/* The tick, worth looking at: a ring that fills with its kind's
            colour — emerald money in, blue money out. */}
        <button className={`w-6 h-6 shrink-0 rounded-full border-2 grid place-items-center
                            text-[13px] leading-none font-bold transition ${
                    i.completed
                      ? (i.kind === 'income'
                          ? 'bg-emerald-400 border-emerald-400 text-ink'
                          : 'bg-vblue border-vblue text-ink')
                      : `border-edge text-transparent ${
                          i.kind === 'income'
                            ? 'hover:border-emerald-400 hover:text-emerald-400/60'
                            : 'hover:border-vblue hover:text-vblue/60'}`}`}
                title={i.kind === 'income'
                  ? (i.completed ? 'Received ✓ — tap to undo' : 'Tap when received')
                  : (i.completed ? 'Paid ✓ — tap to undo' : 'Tap when paid')}
                onClick={(e) => { e.stopPropagation(); tick(i); }}>
          ✓
        </button>
        <span className="flex-1 min-w-0">
          <span className={`block truncate font-medium ${
                  i.completed ? 'text-slate-500 line-through' : ''}`}>
            {i.title}
            {over
              ? <span className="ml-1.5 text-[9px] uppercase tracking-wide text-red-400 border border-red-400/50 rounded px-1.5 py-0.5">OVER</span>
              : dueToday && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-amber-400 border border-amber-400/50 rounded px-1.5 py-0.5">EVENT DUE TODAY</span>}
            {localInc && (
              <span className="ml-1.5 text-[10px] text-slate-500">
                · counts in {i.bank_name} only
              </span>
            )}
            {i.kind === 'income' && i.is_salary && (
              <span className={`ml-1.5 text-[10px] uppercase ${
                      i.salary_primary ? 'text-amber-400' : 'text-vpurple'}`}
                    title={i.salary_primary
                      ? 'Master salary — counts once in the month total'
                      : `Salary paid-in check${i.bank_name ? ` — ${i.bank_name}` : ''}`}>
                {i.salary_primary ? '★ salary' : 'salary check'}
              </span>
            )}
            {i.receipt ? <span className="ml-1.5 text-vblue text-xs">📎</span> : null}
            {i.reduce_n > 0 && (
              <span className="ml-1.5 text-[10px] text-vblue">{i.reduce_n}× paid</span>
            )}
          </span>
          <span className="block text-[11px] text-slate-500 truncate">{stateLine}</span>
          {breakdown && <span className="block text-[10px] text-slate-600 truncate">{breakdown}</span>}
        </span>
        <span className="text-right shrink-0">
          <span className={`block font-semibold group-hover:text-vblue transition ${
                  i.kind === 'income' ? 'text-emerald-400' : ''}`}>
            {fmtR(i.amount)}
          </span>
        </span>
        <span className="text-slate-500 shrink-0 text-sm ml-1">{isOpen ? '▾' : '▸'}</span>
      </div>
      {isOpen && (
        <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-edge" onClick={(e) => e.stopPropagation()}>
          {i.kind === 'income' && (
            <div className="flex items-center gap-2 rounded-xl border border-edge px-3 py-2 mb-3">
              <span className="flex-1 text-xs text-slate-400">{i.completed ? 'Received ✓' : (i.salary_primary ? '★ Ticking received starts the new month.' : 'Tick when it lands.')}</span>
              <button className="border border-emerald-400/50 text-emerald-400 rounded-lg px-3 py-1.5 text-sm hover:bg-emerald-400/10 transition" onClick={() => tick(i)}>{i.completed ? 'Undo' : 'Received'}</button>
            </div>
          )}
          <DetailCard item={i} categories={categories} banks={banks} onChange={onChange} onScan={scanReceipt} onOpenMap={onOpenMap} showHeader={false} />
          {i.kind === 'income' && (
            <button className="text-xs text-slate-500 hover:text-vblue mt-3" onClick={() => setDetail(i)}>Full record — salary, recurring, delete ▸</button>
          )}
        </div>
      )}
      </div>
    );
  }
}
